import React, { useState, useRef, useEffect } from 'react';
import { QRCode } from 'react-qrcode-logo';
import { Box, Typography, Input, Button, Stack, Card, CardContent, Divider, Alert, Snackbar, Modal, ModalDialog, ModalClose, Select, Option } from '@mui/joy';
import { ErrorOutline, CloudUpload, ArrowBack } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { FormElement, FormStep, componentStylesApi, ComponentStyle } from '../services/api';
import { resolveElementMainValue } from '../utils/elementValueSource';
import { isElementVisible } from '../utils/elementVisibility';
import { sanitizeSvg } from '../utils/sanitizeSvg';
import { sanitizeUrl } from '../utils/safeUrl';

interface FormPreviewProps {
  formName: string;
  formDescription?: string;
  elements: FormElement[];
  steps?: FormStep[];
  viewMode?: 'single_page' | 'step_by_step';
  stepAnimation?: 'fade' | 'slide' | 'none';
  activeStepId?: number | string | null; // For builder mode: which step to show
  formStyleId?: number | null;
  formStyles?: {
    showBackButton?: boolean;
    backButtonIcon?: string;
    backButtonText?: string;
  };
  componentStyles?: ComponentStyle[]; // Available component styles for merging
}

interface ValidationErrors {
  [fieldName: string]: string[];
}

/**
 * Custom JS validation is disabled for security (no new Function / eval).
 * Always fails closed so fields with custom rules do not silently pass.
 */
function runCustomValidation(_functionCode: unknown, _value: string): boolean {
  return false;
}

function formatPhoneDisplayValue(val: string): string {
  const digits = String(val).replace(/\D/g, '');
  if (digits.length <= 4) return digits ? `(${digits}` : val;
  if (digits.length <= 7) return `(${digits.slice(0, 4)}) ${digits.slice(4)}`;
  if (digits.length <= 9) return `(${digits.slice(0, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  return `(${digits.slice(0, 4)}) ${digits.slice(4, 7)}-${digits.slice(7, 9)}-${digits.slice(9)}`;
}

/** Format only the first number in a string with locale; keep any prefix/suffix text (e.g. " ARS"). */
function formatFirstNumberInText(
  rawValue: string,
  locale: string,
  minFrac?: number,
  maxFrac?: number,
): string {
  const str = String(rawValue);
  const match = str.match(/-?\d+(?:[.,]\d+)?/);
  if (!match) return str;
  const numStr = match[0].replace(/,/g, '.');
  const num = parseFloat(numStr);
  if (Number.isNaN(num)) return str;
  try {
    const formatted = new Intl.NumberFormat(locale || 'de-DE', {
      minimumFractionDigits: minFrac ?? 2,
      maximumFractionDigits: maxFrac ?? 2,
    }).format(num);
    return str.slice(0, match.index!) + formatted + str.slice(match.index! + match[0].length);
  } catch {
    return str;
  }
}

// Shared helper to get element styles with mobile and hover support
const getElementStylesWithVariants = (
  element: FormElement,
  componentStyles: ComponentStyle[]
): {
  desktop: Record<string, string | number>;
  mobile: Record<string, string | number>;
  hover: Record<string, string | number>;
} => {
  if (!element.styles) return { desktop: {}, mobile: {}, hover: {} };
  
  const nonCssProperties = [
    'validationTriggers',
    'triggerValidationsFor',
    'showStep',
    'componentStyleId',
    'listItems',
    'subtitle',
    'textValue',
    'textWithValueValueFormat',
    'initialTime',
    'timerTimeSource',
    'timerVariableName',
    'timerOnExpiry',
    'timerPosition',
    'copyIcon',
    'copyDoneIcon',
    'copySuccessMessage',
    'copySubtitle',
    'copySectionValueSource',
    'copySectionUrlParamKey',
    'valueSource',
    'urlParamKey',
    'sideTextValueSource',
    'sideTextUrlParamKey',
    'timerUrlParamKey',
    'copySectionValueFormat',
    'copySectionValueNumberLocale',
    'copySectionValueNumberMinFractionDigits',
    'copySectionValueNumberMaxFractionDigits',
    'copySectionHideFirstCharacters',
    'textValueSource',
    'textUrlParamKey',
    'textFormat',
    'textNumberLocale',
    'textNumberMinFractionDigits',
    'textNumberMaxFractionDigits',
    'linkIcon',
    'hideLinkText',
    'linkTarget',
    'linkOnClick',
    'uploadSubtitle',
    'hideLabels',
    'hiddenStepIndices',
    'modalElements',
    'notificationIcon',
    'notificationDescription',
    'notificationDescriptionType',
    'notificationListItems',
    'containerElements',
    'hideFormName',
    'inputType',
    'selectOptions',
    'listenVisibilityChange',
    'disabledClickMessage',
    'inputsGroupInputs',
    'inputsGroupButtonId',
    'inputsGroupAnimation',
    'inputsGroupAnimationDuration',
    'processingTitle',
    'processingStatusSvg',
    'statusTitle',
    'slideshowImages',
    'slideshowPrevSvg',
    'slideshowNextSvg',
    'slideshowButtonBackground',
    'loaderColor',
    'loaderWidth',
    'loaderHeight',
    'stepProgressBarActiveBg',
    'stepProgressBarCompletedBg',
    'stepProgressBarInactiveBg',
    'stepProgressBarCircleWidth',
    'stepProgressBarCircleHeight',
    'stepProgressBarCircleGap',
    'stepProgressBarLineHeight',
    'stepProgressBarLineWidth',
    'stepProgressBarCompletedLineColor',
    'stepProgressBarInactiveLineColor',
    'stepProgressBarHideNumbers',
    'stepProgressBarAlign',
    'showByUrlParamKey',
    'showByUrlParamValue',
    'showByUrlParamContains',
    'showByVariableKey',
    'showByVariableValue',
    'showByVariableContains',
    'showByVariableSource',
    'hideByVariableKey',
    'hideByVariableValue',
    'hideByVariableContains',
    'hideByVariableSource',
    'hideInputIfValidationCorrect',
    'qrEcLevel',
    'qrSize',
    'qrQuietZone',
    'qrBgColor',
    'qrFgColor',
    'qrLogoImageUrl',
    'qrLogoWidth',
    'qrLogoHeight',
    'qrLogoOpacity',
    'qrLogoPadding',
    'qrLogoPaddingStyle',
    'qrRemoveQrCodeBehindLogo',
    'qrStyle',
    'qrEnableCORS',
    'hidden',
  ];

  // Start with styles from component style if componentStyleId exists
  const componentStyleId = element.styles?.componentStyleId;
  const componentStyle = componentStyleId && componentStyles.length > 0
    ? componentStyles.find((s: ComponentStyle) => s.id === componentStyleId)
    : null;
  
  // If componentStyleId exists, ONLY use styles from component_styles table
  if (componentStyleId && componentStyle && componentStyle.styles) {
    const desktopStyles: Record<string, string | number> = {};
    const rawMobileStyles = componentStyle.styles.mobile || {};
    const rawHoverStyles = componentStyle.styles.hover || {};
    
    // Filter out non-CSS properties from mobile and hover styles
    const mobileStyles: Record<string, string | number> = {};
    Object.entries(rawMobileStyles).forEach(([key, value]) => {
      if (!nonCssProperties.includes(key) && value !== null && value !== undefined && value !== '') {
        mobileStyles[key] = value as string | number;
      }
    });
    
    const hoverStyles: Record<string, string | number> = {};
    Object.entries(rawHoverStyles).forEach(([key, value]) => {
      if (!nonCssProperties.includes(key) && value !== null && value !== undefined && value !== '') {
        hoverStyles[key] = value as string | number;
      }
    });
    
    Object.entries(componentStyle.styles).forEach(([key, value]) => {
      if (key !== 'mobile' && key !== 'hover' && !nonCssProperties.includes(key) && value !== null && value !== undefined && value !== '') {
        desktopStyles[key] = value as string | number;
      }
    });
    
    return { desktop: desktopStyles, mobile: mobileStyles, hover: hoverStyles };
  }

  // If no componentStyleId, use element's own styles (backward compatibility)
  const desktopStyles: Record<string, string | number> = {};
  const rawMobileStyles = (element.styles?.mobile as Record<string, string | number>) || {};
  const rawHoverStyles = (element.styles?.hover as Record<string, string | number>) || {};
  
  // Filter out non-CSS properties from mobile and hover styles
  const mobileStyles: Record<string, string | number> = {};
  Object.entries(rawMobileStyles).forEach(([key, value]) => {
    if (!nonCssProperties.includes(key) && value !== null && value !== undefined && value !== '') {
      mobileStyles[key] = value as string | number;
    }
  });
  
  const hoverStyles: Record<string, string | number> = {};
  Object.entries(rawHoverStyles).forEach(([key, value]) => {
    if (!nonCssProperties.includes(key) && value !== null && value !== undefined && value !== '') {
      hoverStyles[key] = value as string | number;
    }
  });
  
  Object.entries(element.styles || {}).forEach(([key, value]) => {
    if (key !== 'mobile' && key !== 'hover' && !nonCssProperties.includes(key) && value !== null && value !== undefined && value !== '') {
      desktopStyles[key] = value as string | number;
    }
  });

  return { desktop: desktopStyles, mobile: mobileStyles, hover: hoverStyles };
};

// Shared helper to combine styles with mobile and hover support
const combineStylesWithVariants = (
  desktop: Record<string, string | number>,
  mobile: Record<string, string | number>,
  hover: Record<string, string | number>,
  isButton = false
): any => {
  const hasMobile = Object.keys(mobile).length > 0;
  const hasHover = isButton && Object.keys(hover).length > 0;
  
  if (!hasMobile && !hasHover) {
    return desktop;
  }
  
  const sx: any = { ...desktop };
  
  if (hasMobile) {
    sx['@media (max-width: 767px)'] = mobile;
  }
  
  if (hasHover) {
    sx['&:hover'] = hover;
  }
  
  return sx;
};

const UploadFileElement: React.FC<{ element: FormElement; componentStyles?: ComponentStyle[] }> = ({ element, componentStyles = [] }) => {
  const { desktop, mobile, hover } = getElementStylesWithVariants(element, componentStyles);
  
  // Helper to separate text styles from container styles
  const getTextStyles = (allStyles: Record<string, string | number>): Record<string, string | number> => {
    const textStyleKeys = ['color', 'fontSize', 'fontWeight', 'fontFamily', 'lineHeight', 'textAlign', 'textDecoration'];
    const textStyles: Record<string, string | number> = {};
    textStyleKeys.forEach(key => {
      if (allStyles[key] !== undefined && allStyles[key] !== null && allStyles[key] !== '') {
        textStyles[key] = allStyles[key];
      }
    });
    return textStyles;
  };

  const getContainerStyles = (allStyles: Record<string, string | number>): Record<string, string | number> => {
    const textStyleKeys = ['color', 'fontSize', 'fontWeight', 'fontFamily', 'lineHeight', 'textAlign', 'textDecoration'];
    const containerStyles: Record<string, string | number> = {};
    Object.entries(allStyles).forEach(([key, value]) => {
      if (!textStyleKeys.includes(key) && value !== undefined && value !== null && value !== '') {
        containerStyles[key] = value;
      }
    });
    return containerStyles;
  };
  
  const textStyles = combineStylesWithVariants(
    getTextStyles(desktop),
    getTextStyles(mobile),
    getTextStyles(hover)
  );
  const containerStyles = combineStylesWithVariants(
    getContainerStyles(desktop),
    getContainerStyles(mobile),
    getContainerStyles(hover)
  );
  const title = element.label || 'Upload File';
  const subtitle = element.styles?.uploadSubtitle || '';
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'];
    const allowedExtensions = ['.png', '.jpg', '.jpeg', '.pdf'];
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();

    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      toast.error('Invalid file type. Only PNG, JPG, JPEG, and PDF files are allowed.');
      return;
    }

    // Validate file size (5MB = 5 * 1024 * 1024 bytes)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast.error('File size exceeds 5MB limit. Please choose a smaller file.');
      return;
    }

    setSelectedFile(file);
    toast.success(`File selected: ${file.name}`);
  };

  return (
    <Box sx={{ mb: 2, ...containerStyles }} id={element.htmlId || undefined} className={element.className || undefined}>
      {title && (
        <Typography level="title-sm" sx={{ mb: 1, fontWeight: 'md', ...textStyles }}>
          {title}
        </Typography>
      )}
      {subtitle && (
        <Typography level="body-sm" sx={{ mb: 2, color: 'text.secondary', ...textStyles }}>
          {subtitle}
        </Typography>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept=".png,.jpg,.jpeg,.pdf"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
      <Box
        onClick={() => fileInputRef.current?.click()}
        sx={{
          p: 3,
          border: '2px dashed',
          borderColor: selectedFile ? 'success.500' : 'divider',
          borderRadius: 'sm',
          textAlign: 'center',
          bgcolor: selectedFile ? 'success.softBg' : 'background.level1',
          cursor: 'pointer',
          transition: 'all 0.2s',
          ...containerStyles,
          '&:hover': {
            borderColor: 'primary.500',
            bgcolor: 'background.level2',
            ...(hover && Object.keys(hover).length > 0 && getContainerStyles(hover)),
          },
        }}
      >
        <CloudUpload sx={{ fontSize: 40, color: 'text.tertiary', mb: 1 }} />
        {selectedFile ? (
          <Typography level="body-md" sx={{ color: 'success.600', fontWeight: 'md' }}>
            {selectedFile.name}
          </Typography>
        ) : (
          <>
            <Typography level="body-md" sx={{ mb: 0.5 }}>
              Click to upload file
            </Typography>
            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
              PNG, JPG, JPEG, or PDF (max 5MB)
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
};

const IMAGE_WIDTH = 203;
const IMAGE_GAP = 10;

export const SlideshowElement: React.FC<{
  element: FormElement;
  slideId: string;
  containerStyles: Record<string, any>;
  viewportStyles: Record<string, any>;
  buttonBackground: string;
  htmlAttributes: Record<string, any>;
}> = ({ element, slideId, containerStyles, viewportStyles, buttonBackground, htmlAttributes }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const images = element.styles?.slideshowImages || [];
  const prevSvg = element.styles?.slideshowPrevSvg ?? '';
  const nextSvg = element.styles?.slideshowNextSvg ?? '';
  const bg = buttonBackground || 'rgb(11, 111, 199)';

  useEffect(() => {
    const el = scrollRef.current;
    const prevBtn = document.getElementById(`${slideId}-prev`);
    const nextBtn = document.getElementById(`${slideId}-next`);
    if (!el || !prevBtn || !nextBtn) return;
    const getCurrentIndex = () => {
      const total = IMAGE_WIDTH + IMAGE_GAP;
      return Math.round(el.scrollLeft / total);
    };
    const onPrev = () => {
      const idx = getCurrentIndex();
      if (idx > 0) {
        el.scrollTo({ left: (idx - 1) * (IMAGE_WIDTH + IMAGE_GAP), behavior: 'smooth' });
      }
    };
    const onNext = () => {
      const idx = getCurrentIndex();
      if (idx < images.length - 1) {
        el.scrollTo({ left: (idx + 1) * (IMAGE_WIDTH + IMAGE_GAP), behavior: 'smooth' });
      }
    };
    prevBtn.addEventListener('click', onPrev);
    nextBtn.addEventListener('click', onNext);
    return () => {
      prevBtn.removeEventListener('click', onPrev);
      nextBtn.removeEventListener('click', onNext);
    };
  }, [slideId, images.length]);

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        mb: 5,
        gap: 1.25,
        ...containerStyles,
      }}
      {...htmlAttributes}
    >
      <button
        id={`${slideId}-prev`}
        type="button"
        aria-label="Previous"
        className="slideshow-btn slideshow-prev"
        style={{
          position: 'absolute',
          zIndex: 10,
          right: 46,
          left: 'auto',
          top: '100%',
          transform: 'translateY(-50%)',
          width: 30,
          height: 30,
          borderRadius: '50%',
          backgroundColor: bg,
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
          transition: 'all 0.2s',
        }}
      >
        {prevSvg ? <Box component="span" sx={{ width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }} dangerouslySetInnerHTML={{ __html: sanitizeSvg(prevSvg) }} /> : null}
      </button>
      <Box
        className="slideshow-viewport"
        sx={{
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          overflow: 'hidden',
          position: 'relative',
          bgcolor: 'white',
          p: 1.25,
          border: '1px solid',
          borderColor: '#e0e0e0',
          borderRadius: '20px',
          ...viewportStyles,
        }}
      >
        <Box
          ref={scrollRef}
          className="instruction-slideshow"
          sx={{
            display: 'flex',
            overflowX: 'auto',
            overflowY: 'hidden',
            scrollBehavior: 'smooth',
            width: '100%',
            height: 400,
            gap: 0,
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {images.length === 0 ? (
            <Typography level="body-sm" sx={{ color: 'text.tertiary', alignSelf: 'center', mx: 'auto' }}>No images</Typography>
          ) : (
            images.map((src: string, i: number) => (
              <Box
                key={i}
                component="img"
                src={src}
                alt={`Slide ${i + 1}`}
                sx={{
                  height: 400,
                  width: IMAGE_WIDTH,
                  borderRadius: '8px',
                  display: 'block',
                  objectFit: 'contain',
                  mr: 1.25,
                  flexShrink: 0,
                }}
              />
            ))
          )}
        </Box>
      </Box>
      <button
        id={`${slideId}-next`}
        type="button"
        aria-label="Next"
        className="slideshow-btn slideshow-next"
        style={{
          position: 'absolute',
          right: 10,
          top: '100%',
          transform: 'translateY(-50%)',
          zIndex: 10,
          width: 30,
          height: 30,
          borderRadius: '50%',
          backgroundColor: bg,
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
          transition: 'all 0.2s',
        }}
      >
        {nextSvg ? <Box component="span" sx={{ width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }} dangerouslySetInnerHTML={{ __html: sanitizeSvg(nextSvg) }} /> : null}
      </button>
    </Box>
  );
};

const TimerElement: React.FC<{
  element: FormElement;
  componentStyles?: ComponentStyle[];
  onExpiryShowLink?: (linkElementId: number) => void;
}> = ({ element, componentStyles = [], onExpiryShowLink }) => {
  const { desktop, mobile, hover } = getElementStylesWithVariants(element, componentStyles);
  
  // Helper to separate text styles from container styles
  const getTextStyles = (allStyles: Record<string, string | number>): Record<string, string | number> => {
    const textStyleKeys = ['color', 'fontSize', 'fontWeight', 'fontFamily', 'lineHeight', 'textAlign', 'textDecoration'];
    const textStyles: Record<string, string | number> = {};
    textStyleKeys.forEach(key => {
      if (allStyles[key] !== undefined && allStyles[key] !== null && allStyles[key] !== '') {
        textStyles[key] = allStyles[key];
      }
    });
    return textStyles;
  };

  const getContainerStyles = (allStyles: Record<string, string | number>): Record<string, string | number> => {
    const textStyleKeys = ['color', 'fontSize', 'fontWeight', 'fontFamily', 'lineHeight', 'textAlign', 'textDecoration'];
    const containerStyles: Record<string, string | number> = {};
    Object.entries(allStyles).forEach(([key, value]) => {
      if (!textStyleKeys.includes(key) && value !== undefined && value !== null && value !== '') {
        containerStyles[key] = value;
      }
    });
    return containerStyles;
  };

  const textStyles = combineStylesWithVariants(
    getTextStyles(desktop),
    getTextStyles(mobile),
    getTextStyles(hover)
  );
  const containerStyles = combineStylesWithVariants(
    getContainerStyles(desktop),
    getContainerStyles(mobile),
    getContainerStyles(hover)
  );
  
  const initialTime = element.styles?.initialTime || element.value || '00:00';
  const onExpiry = element.styles?.timerOnExpiry ?? 'show_00';
  const linkElementIdAfterExpiry = element.styles?.linkElementIdAfterExpiry;
  const timerPosition = element.styles?.timerPosition ?? 'inline';
  const timerSvgBefore = element.styles?.timerSvgBefore;
  const [timeLeft, setTimeLeft] = useState<string>(initialTime);
  const [totalSeconds, setTotalSeconds] = useState<number>(() => {
    const [minutes, seconds] = initialTime.split(':').map(Number);
    return (minutes || 0) * 60 + (seconds || 0);
  });
  const expiryFiredRef = React.useRef(false);

  useEffect(() => {
    const [minutes, seconds] = initialTime.split(':').map(Number);
    const newTotalSeconds = (minutes || 0) * 60 + (seconds || 0);
    setTotalSeconds(newTotalSeconds);
    setTimeLeft(initialTime);
    expiryFiredRef.current = false;
  }, [initialTime]);

  useEffect(() => {
    if (totalSeconds <= 0) {
      setTimeLeft('00:00');
      if (onExpiry === 'show_link' && linkElementIdAfterExpiry && onExpiryShowLink && !expiryFiredRef.current) {
        expiryFiredRef.current = true;
        onExpiryShowLink(Number(linkElementIdAfterExpiry));
      }
      return;
    }

    const interval = setInterval(() => {
      setTotalSeconds((prev) => {
        if (prev <= 0) return 0;
        const newSeconds = prev - 1;
        const minutes = Math.floor(newSeconds / 60);
        const seconds = newSeconds % 60;
        setTimeLeft(`${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`);
        if (newSeconds === 0 && onExpiry === 'show_link' && linkElementIdAfterExpiry && onExpiryShowLink && !expiryFiredRef.current) {
          expiryFiredRef.current = true;
          onExpiryShowLink(Number(linkElementIdAfterExpiry));
        }
        return newSeconds;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [totalSeconds, onExpiry, onExpiryShowLink, linkElementIdAfterExpiry]);

  const positionSx =
    timerPosition === 'top-left'
      ? { position: 'absolute' as const, top: 8, left: 8 }
      : timerPosition === 'top-right'
        ? { position: 'absolute' as const, top: 8, right: 8 }
        : timerPosition === 'top'
          ? { position: 'absolute' as const, top: 8, left: '50%', transform: 'translateX(-50%)' }
          : {};

  return (
    <Box
      key={element.id || 'timer'}
      sx={{
        mb: timerPosition === 'inline' ? 2 : 0,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        p: 2,
        ...containerStyles,
        ...positionSx,
      }}
      id={element.htmlId || undefined}
      className={element.className || undefined}
    >
      {timerSvgBefore && (
        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, '& svg': { display: 'block' } }} dangerouslySetInnerHTML={{ __html: sanitizeSvg(timerSvgBefore) }} />
      )}
      <Typography level="h3" sx={{ fontFamily: 'monospace', fontWeight: 'bold', ...textStyles }}>
        {timeLeft}
      </Typography>
    </Box>
  );
};

const CopySectionElement: React.FC<{ element: FormElement; componentStyles?: ComponentStyle[] }> = ({ element, componentStyles = [] }) => {
  const { desktop, mobile, hover } = getElementStylesWithVariants(element, componentStyles);
  
  // Helper to separate text styles from container styles
  const getTextStyles = (allStyles: Record<string, string | number>): Record<string, string | number> => {
    const textStyleKeys = ['color', 'fontSize', 'fontWeight', 'fontFamily', 'lineHeight', 'textAlign', 'textDecoration'];
    const textStyles: Record<string, string | number> = {};
    textStyleKeys.forEach(key => {
      if (allStyles[key] !== undefined && allStyles[key] !== null && allStyles[key] !== '') {
        textStyles[key] = allStyles[key];
      }
    });
    return textStyles;
  };

  const copySectionPartKeys = ['copySectionTitleColor', 'copySectionTitleFontSize', 'copySectionTitleFontWeight', 'copySectionSubtitleColor', 'copySectionSubtitleFontSize', 'copySectionValueColor', 'copySectionValueFontSize', 'copySectionValueFontWeight'];
  const getContainerStyles = (allStyles: Record<string, string | number>): Record<string, string | number> => {
    const textStyleKeys = ['color', 'fontSize', 'fontWeight', 'fontFamily', 'lineHeight', 'textAlign', 'textDecoration'];
    const containerStyles: Record<string, string | number> = {};
    Object.entries(allStyles).forEach(([key, value]) => {
      if (!textStyleKeys.includes(key) && !copySectionPartKeys.includes(key) && value !== undefined && value !== null && value !== '') {
        containerStyles[key] = value;
      }
    });
    return containerStyles;
  };
  const getPartStyles = (allStyles: Record<string, string | number>, prefix: string): Record<string, string | number> => {
    const part: Record<string, string | number> = {};
    Object.entries(allStyles).forEach(([key, value]) => {
      if (key.startsWith(prefix) && value !== undefined && value !== null && value !== '') {
        const cssKey = key === prefix + 'Color' ? 'color' : key === prefix + 'FontSize' ? 'fontSize' : key === prefix + 'FontWeight' ? 'fontWeight' : null;
        if (cssKey) part[cssKey] = value;
      }
    });
    return part;
  };

  const textStyles = combineStylesWithVariants(
    getTextStyles(desktop),
    getTextStyles(mobile),
    getTextStyles(hover)
  );
  const containerStyles = combineStylesWithVariants(
    getContainerStyles(desktop),
    getContainerStyles(mobile),
    getContainerStyles(hover),
    false
  );
  const titleStyles = combineStylesWithVariants(
    getPartStyles(desktop, 'copySectionTitle'),
    getPartStyles(mobile, 'copySectionTitle'),
    getPartStyles(hover, 'copySectionTitle'),
    false
  );
  const subtitlePartStyles = combineStylesWithVariants(
    getPartStyles(desktop, 'copySectionSubtitle'),
    getPartStyles(mobile, 'copySectionSubtitle'),
    getPartStyles(hover, 'copySectionSubtitle'),
    false
  );
  const valuePartStyles = combineStylesWithVariants(
    getPartStyles(desktop, 'copySectionValue'),
    getPartStyles(mobile, 'copySectionValue'),
    getPartStyles(hover, 'copySectionValue'),
    false
  );
  const hoverContainer = getContainerStyles(hover);
  const hoverText = getTextStyles(hover);
  const defaultHover = { bgcolor: 'background.level2', borderColor: 'primary.500' };
  const mergedHover = { ...defaultHover, ...hoverContainer, ...hoverText };
  
  const copyValueRaw = resolveElementMainValue(element, undefined, '');
  const hideFirstN = typeof element.styles?.copySectionHideFirstCharacters === 'number' && element.styles.copySectionHideFirstCharacters > 0
    ? element.styles.copySectionHideFirstCharacters
    : 0;
  const copyValueTruncated = hideFirstN > 0 ? String(copyValueRaw).slice(hideFirstN) : copyValueRaw;
  const formatAsNumber = (val: string, locale: string, minFrac?: number, maxFrac?: number): string => {
    const num = parseFloat(String(val).replace(/,/g, '.'));
    if (Number.isNaN(num)) return val;
    try {
      return new Intl.NumberFormat(locale || 'de-DE', {
        minimumFractionDigits: minFrac ?? 2,
        maximumFractionDigits: maxFrac ?? 2,
      }).format(num);
    } catch {
      return val;
    }
  };
  const formatPhoneDisplay = (val: string): string => {
    const digits = String(val).replace(/\D/g, '');
    if (digits.length <= 4) return digits ? `(${digits}` : val;
    if (digits.length <= 7) return `(${digits.slice(0, 4)}) ${digits.slice(4)}`;
    if (digits.length <= 9) return `(${digits.slice(0, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    return `(${digits.slice(0, 4)}) ${digits.slice(4, 7)}-${digits.slice(7, 9)}-${digits.slice(9)}`;
  };
  const copyValueFormat = element.styles?.copySectionValueFormat ?? 'none';
  const copyValueLocale = element.styles?.copySectionValueNumberLocale || 'de-DE';
  const copyValueMinFrac = element.styles?.copySectionValueNumberMinFractionDigits;
  const copyValueMaxFrac = element.styles?.copySectionValueNumberMaxFractionDigits;
  const copyValueDisplay = copyValueFormat === 'number'
    ? formatAsNumber(copyValueTruncated, copyValueLocale, copyValueMinFrac, copyValueMaxFrac)
    : copyValueFormat === 'phone'
      ? formatPhoneDisplay(copyValueTruncated)
      : copyValueTruncated;
  const copyValueToCopy = copyValueFormat === 'phone'
    ? String(copyValueTruncated || '').replace(/\D/g, '')
    : copyValueFormat === 'number'
      ? copyValueTruncated
      : copyValueTruncated;
  const copyIcon = element.styles?.copyIcon || '';
  const copyDoneIcon = element.styles?.copyDoneIcon || '';
  const copySubtitle = element.styles?.copySubtitle || '';
  const successMessage = element.styles?.copySuccessMessage || '{copied_value} copied';
  const [showCopyDone, setShowCopyDone] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  const handleCopy = async () => {
    if (!copyValueTruncated && copyValueFormat !== 'phone') return;
    if (copyValueFormat === 'phone' && !copyValueToCopy) return;

    try {
      await navigator.clipboard.writeText(copyValueToCopy);
      
      const message = successMessage.replace('{copied_value}', copyValueToCopy);
      setAlertMessage(message);
      setShowAlert(true);
      
      setShowCopyDone(true);
      setTimeout(() => {
        setShowCopyDone(false);
      }, 1000);
      
      setTimeout(() => {
        setShowAlert(false);
      }, 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error('Failed to copy to clipboard');
    }
  };

  return (
    <>
      <Box sx={{ mb: 2 }} id={element.htmlId || undefined} className={element.className || undefined}>
        {(element.label !== undefined && element.label !== null && element.label !== '') && (
          <Typography level="title-sm" sx={{ mb: 1, fontWeight: 'md', ...textStyles, ...titleStyles }}>
            {element.label}
          </Typography>
        )}
        <Box
          onClick={handleCopy}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 1.5,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 'sm',
            bgcolor: 'background.level1',
            cursor: 'pointer',
            transition: 'all 0.2s',
            position: 'relative',
            ...containerStyles,
            '&:hover': mergedHover,
          }}
        >
          {copySubtitle && (
            <Typography level="body-sm" sx={{ color: 'text.secondary', mr: 2, ...textStyles, ...subtitlePartStyles }}>
              {copySubtitle}
            </Typography>
          )}
          <Typography level="body-md" sx={{ flex: 1, mr: element.styles?.hideCopyIcons ? 0 : 2, wordBreak: 'break-all', ...textStyles, ...valuePartStyles }}>
            {copyValueDisplay || 'Value to copy'}
          </Typography>
          {!element.styles?.hideCopyIcons && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  minWidth: '40px',
                  justifyContent: 'center',
                  position: 'relative',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: showCopyDone ? 0 : 1,
                    transition: 'opacity 0.2s',
                    position: 'absolute',
                  }}
                >
                  {copyIcon ? (
                    <Box
                      sx={{ width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      dangerouslySetInnerHTML={{ __html: sanitizeSvg(copyIcon) }}
                    />
                  ) : (
                    <Typography level="body-xs">Copy</Typography>
                  )}
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: showCopyDone ? 1 : 0,
                    transition: 'opacity 0.2s',
                    position: 'absolute',
                    pointerEvents: 'none',
                  }}
                >
                  {copyDoneIcon ? (
                    <Box
                      sx={{ width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      dangerouslySetInnerHTML={{ __html: sanitizeSvg(copyDoneIcon) }}
                    />
                  ) : (
                    <Typography level="body-xs" sx={{ color: 'success.500' }}>
                      ✓
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
      <Snackbar
        open={showAlert}
        autoHideDuration={2000}
        color="success"
        variant="solid"
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          position: 'fixed',
          top: 20,
          zIndex: 9999,
        }}
      >
        {alertMessage}
      </Snackbar>
    </>
  );
};

function pickMergedRootStyle(element: FormElement, componentStyles: ComponentStyle[], key: string): unknown {
  const el = element.styles || {};
  const cid = el.componentStyleId;
  const cs = cid != null && componentStyles.length > 0 ? componentStyles.find((s) => s.id === cid) : null;
  const baseRoot = (cs?.styles as Record<string, unknown>) || {};
  const ev = (el as Record<string, unknown>)[key];
  if (ev !== undefined && ev !== null && ev !== '') return ev;
  const bv = baseRoot[key];
  if (bv !== undefined && bv !== null && bv !== '') return bv;
  return undefined;
}

const QR_STYLE_CONTAINER_EXCLUDE = new Set([
  'qrEcLevel',
  'qrSize',
  'qrQuietZone',
  'qrBgColor',
  'qrFgColor',
  'qrLogoImageUrl',
  'qrLogoWidth',
  'qrLogoHeight',
  'qrLogoOpacity',
  'qrLogoPadding',
  'qrLogoPaddingStyle',
  'qrRemoveQrCodeBehindLogo',
  'qrStyle',
  'qrEnableCORS',
]);

export const QrCodeFormElement: React.FC<{
  element: FormElement;
  componentStyles?: ComponentStyle[];
  replaceVariables?: (t: string) => string;
}> = ({ element, componentStyles = [], replaceVariables }) => {
  const { desktop, mobile, hover } = getElementStylesWithVariants(element, componentStyles);
  const getTextStyles = (allStyles: Record<string, string | number>): Record<string, string | number> => {
    const textStyleKeys = ['color', 'fontSize', 'fontWeight', 'fontFamily', 'lineHeight', 'textAlign', 'textDecoration'];
    const textStyles: Record<string, string | number> = {};
    textStyleKeys.forEach((key) => {
      if (allStyles[key] !== undefined && allStyles[key] !== null && allStyles[key] !== '') {
        textStyles[key] = allStyles[key];
      }
    });
    return textStyles;
  };
  const getContainerStyles = (allStyles: Record<string, string | number>): Record<string, string | number> => {
    const textStyleKeys = ['color', 'fontSize', 'fontWeight', 'fontFamily', 'lineHeight', 'textAlign', 'textDecoration'];
    const containerStyles: Record<string, string | number> = {};
    Object.entries(allStyles).forEach(([key, value]) => {
      if (QR_STYLE_CONTAINER_EXCLUDE.has(key)) return;
      if (!textStyleKeys.includes(key) && value !== undefined && value !== null && value !== '') {
        containerStyles[key] = value;
      }
    });
    return containerStyles;
  };
  const wrapperSx = combineStylesWithVariants(
    getContainerStyles(desktop),
    getContainerStyles(mobile),
    getContainerStyles(hover),
    false,
  );
  const labelTextStyles = getTextStyles(desktop);
  const alignRaw = String(labelTextStyles.textAlign || getContainerStyles(desktop).textAlign || 'center').toLowerCase();
  const alignItems =
    alignRaw === 'right' ? 'flex-end' : alignRaw === 'left' ? 'flex-start' : 'center';

  const encodedValue = resolveElementMainValue(element, replaceVariables, 'https://example.com') || 'https://example.com';

  const logoRaw = String(element.styles?.qrLogoImageUrl ?? '').trim();
  const logoImage =
    logoRaw === '' ? undefined : replaceVariables ? replaceVariables(logoRaw) : logoRaw;

  const num = (key: string, fallback: number) => {
    const v = pickMergedRootStyle(element, componentStyles, key);
    const n = typeof v === 'number' ? v : parseFloat(String(v));
    return Number.isFinite(n) ? n : fallback;
  };

  const size = Math.min(400, Math.max(80, Math.round(num('qrSize', 180))));
  const quietZone = Math.max(0, Math.round(num('qrQuietZone', 10)));
  const ecRaw = String(pickMergedRootStyle(element, componentStyles, 'qrEcLevel') || 'M').toUpperCase();
  const ecLevel = (['L', 'M', 'Q', 'H'].includes(ecRaw) ? ecRaw : 'M') as 'L' | 'M' | 'Q' | 'H';
  const bgColor = String(pickMergedRootStyle(element, componentStyles, 'qrBgColor') || '#FFFFFF');
  const fgColor = String(pickMergedRootStyle(element, componentStyles, 'qrFgColor') || '#000000');
  const qrStyleModRaw = String(pickMergedRootStyle(element, componentStyles, 'qrStyle') || 'squares').toLowerCase();
  const qrStyleMod = (['squares', 'dots', 'fluid'].includes(qrStyleModRaw) ? qrStyleModRaw : 'squares') as
    | 'squares'
    | 'dots'
    | 'fluid';

  const lw = pickMergedRootStyle(element, componentStyles, 'qrLogoWidth');
  const lh = pickMergedRootStyle(element, componentStyles, 'qrLogoHeight');
  const logoWidthNum = lw != null && String(lw).trim() !== '' ? Number(lw) : undefined;
  const logoHeightNum = lh != null && String(lh).trim() !== '' ? Number(lh) : undefined;
  const logoOpacity = Math.min(1, Math.max(0, num('qrLogoOpacity', 1)));
  const logoPaddingRaw = num('qrLogoPadding', 0);
  const logoPadding = logoPaddingRaw > 0 ? logoPaddingRaw : undefined;
  const lps = String(pickMergedRootStyle(element, componentStyles, 'qrLogoPaddingStyle') || 'square');
  const logoPaddingStyle = (lps === 'circle' ? 'circle' : 'square') as 'square' | 'circle';
  const removeQrCodeBehindLogo =
    pickMergedRootStyle(element, componentStyles, 'qrRemoveQrCodeBehindLogo') === true ||
    pickMergedRootStyle(element, componentStyles, 'qrRemoveQrCodeBehindLogo') === 'true';
  const enableCORS =
    pickMergedRootStyle(element, componentStyles, 'qrEnableCORS') === true ||
    pickMergedRootStyle(element, componentStyles, 'qrEnableCORS') === 'true';

  return (
    <Box
      sx={{
        mb: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: alignItems,
        ...wrapperSx,
      }}
      id={element.htmlId || undefined}
      className={element.className || undefined}
    >
      {(element.label !== undefined && element.label !== null && element.label !== '') && (
        <Typography level="title-sm" sx={{ mb: 1, fontWeight: 'md', ...labelTextStyles }}>
          {element.label}
        </Typography>
      )}
      <Box sx={{ display: 'flex', justifyContent: alignItems, width: '100%', maxWidth: '100%' }}>
        <QRCode
          value={encodedValue}
          size={size}
          quietZone={quietZone}
          ecLevel={ecLevel}
          bgColor={bgColor}
          fgColor={fgColor}
          qrStyle={qrStyleMod}
          logoImage={logoImage}
          logoWidth={Number.isFinite(logoWidthNum as number) ? (logoWidthNum as number) : undefined}
          logoHeight={Number.isFinite(logoHeightNum as number) ? (logoHeightNum as number) : undefined}
          logoOpacity={logoOpacity}
          logoPadding={logoPadding}
          logoPaddingStyle={logoPaddingStyle}
          removeQrCodeBehindLogo={removeQrCodeBehindLogo}
          enableCORS={enableCORS}
        />
      </Box>
    </Box>
  );
};

const FormPreview: React.FC<FormPreviewProps> = ({ 
  formName, 
  formDescription, 
  elements, 
  steps,
  viewMode = 'single_page',
  stepAnimation = 'fade',
  activeStepId,
  formStyleId,
  formStyles,
  componentStyles = [],
}) => {
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [disabledDueToValidationButtonId, setDisabledDueToValidationButtonId] = useState<number | null>(null);
  const [reEnableOnVisibilityChange, setReEnableOnVisibilityChange] = useState<boolean>(false);
  // When true, buttons with "Listen for visibility change" are enabled (page visible or window focused)
  const [isPageVisibleOrFocused, setIsPageVisibleOrFocused] = useState<boolean>(false);
  const [inputsGroupCurrentIndex, setInputsGroupCurrentIndex] = useState<Record<number, number>>({});
  const [inputsGroupVisible, setInputsGroupVisible] = useState<Record<number, boolean>>({});
  const [currentStepIndex, setCurrentStepIndex] = useState<number>(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const [loadedComponentStyles, setLoadedComponentStyles] = useState<ComponentStyle[]>(componentStyles);
  const [revealedLinkIds, setRevealedLinkIds] = useState<Set<number>>(new Set());

  const getElementById = (id: number): FormElement | undefined => {
    const targetId = Number(id);
    if (!Number.isFinite(targetId)) return undefined;
    for (const el of elements) {
      if (el.id != null && Number(el.id) === targetId) return el;
      if (el.type === 'container' && el.styles?.containerElements) {
        const inner = (el.styles.containerElements as FormElement[]).find(
          (c) => c.id != null && Number(c.id) === targetId,
        );
        if (inner) return inner;
      }
    }
    return undefined;
  };

  // Get form style
  const formStyle = formStyleId && loadedComponentStyles.length > 0
    ? loadedComponentStyles.find((s: ComponentStyle) => s.id === formStyleId)
    : null;
  
  // Get form container styles with mobile support
  const getFormStyles = (): any => {
    if (formStyle && formStyle.styles) {
      const nonCssProperties = ['hideFormName'];
      
      // Extract desktop styles
      const desktopStyles: Record<string, string | number> = {};
      Object.entries(formStyle.styles).forEach(([key, value]) => {
        if (key !== 'mobile' && key !== 'hover' && !nonCssProperties.includes(key) && value !== null && value !== undefined && value !== '') {
          desktopStyles[key] = value as string | number;
        }
      });
      
      // Extract mobile styles
      const rawMobileStyles = formStyle.styles.mobile || {};
      const mobileStyles: Record<string, string | number> = {};
      Object.entries(rawMobileStyles).forEach(([key, value]) => {
        if (!nonCssProperties.includes(key) && value !== null && value !== undefined && value !== '') {
          mobileStyles[key] = value as string | number;
        }
      });
      
      // Extract hover styles (for consistency, though form probably won't use hover)
      const rawHoverStyles = formStyle.styles.hover || {};
      const hoverStyles: Record<string, string | number> = {};
      Object.entries(rawHoverStyles).forEach(([key, value]) => {
        if (!nonCssProperties.includes(key) && value !== null && value !== undefined && value !== '') {
          hoverStyles[key] = value as string | number;
        }
      });
      
      // Combine desktop and mobile styles using the same logic as other components
      return combineStylesWithVariants(desktopStyles, mobileStyles, hoverStyles, false);
    }
    return {};
  };

  // Check if form name should be hidden
  const shouldHideFormName = formStyle?.styles?.hideFormName === true || formStyle?.styles?.hideFormName === 'true';

  // Get step style for a step with mobile support
  const getStepStyles = (step: FormStep): any => {
    if (step.stepStyleId && loadedComponentStyles.length > 0) {
      const stepStyle = loadedComponentStyles.find((s: ComponentStyle) => s.id === step.stepStyleId);
      if (stepStyle && stepStyle.styles) {
        // Extract desktop styles
        const desktopStyles: Record<string, string | number> = {};
        Object.entries(stepStyle.styles).forEach(([key, value]) => {
          if (key !== 'mobile' && key !== 'hover' && value !== null && value !== undefined && value !== '') {
            desktopStyles[key] = value as string | number;
          }
        });
        
        // Extract mobile styles
        const rawMobileStyles = stepStyle.styles.mobile || {};
        const mobileStyles: Record<string, string | number> = {};
        Object.entries(rawMobileStyles).forEach(([key, value]) => {
          if (value !== null && value !== undefined && value !== '') {
            mobileStyles[key] = value as string | number;
          }
        });
        
        // Extract hover styles
        const rawHoverStyles = stepStyle.styles.hover || {};
        const hoverStyles: Record<string, string | number> = {};
        Object.entries(rawHoverStyles).forEach(([key, value]) => {
          if (value !== null && value !== undefined && value !== '') {
            hoverStyles[key] = value as string | number;
          }
        });
        
        // Combine desktop and mobile styles using the same logic as other components
        return combineStylesWithVariants(desktopStyles, mobileStyles, hoverStyles, false);
      }
    }
    return {};
  };

  // Load component styles if not provided, and reload when updated
  useEffect(() => {
    const loadStyles = () => {
      // Always reload from API to get latest styles, even if provided as prop
      componentStylesApi.getAll().then((styles) => {
        setLoadedComponentStyles(styles);
      }).catch((error) => {
        console.error('Failed to load component styles:', error);
        // Fallback to provided styles if API fails
        if (componentStyles.length > 0) {
          setLoadedComponentStyles(componentStyles);
        }
      });
    };
    
    // Always load styles initially to get latest data
    loadStyles();
    
    // Also use provided styles if available (for immediate display)
    if (componentStyles.length > 0) {
      setLoadedComponentStyles(componentStyles);
    }
    
    // Listen for component style updates via BroadcastChannel
    let broadcastChannel: BroadcastChannel | null = null;
    if (typeof BroadcastChannel !== 'undefined') {
      broadcastChannel = new BroadcastChannel('component-styles-updated');
      broadcastChannel.onmessage = (event) => {
        if (event.data.type === 'component-style-saved') {
          console.log('Component style updated, reloading styles...', event.data);
          loadStyles();
        }
      };
    }
    
    // Listen for localStorage changes (fallback for cross-tab communication)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'component-styles-updated') {
        console.log('Storage event detected, reloading component styles...');
        loadStyles();
      }
    };
    window.addEventListener('storage', handleStorageChange);
    
    // Also listen for custom events (for same-tab communication)
    const handleCustomStorage = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail?.key === 'component-styles-updated') {
        console.log('Custom storage event detected, reloading component styles...');
        loadStyles();
      }
    };
    window.addEventListener('component-styles-updated', handleCustomStorage as EventListener);
    
    // Reload component styles when window regains focus (user might have edited styles in another tab)
    const handleFocus = () => {
      loadStyles();
    };
    window.addEventListener('focus', handleFocus);
    
    return () => {
      if (broadcastChannel) {
        broadcastChannel.close();
      }
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('component-styles-updated', handleCustomStorage as EventListener);
      window.removeEventListener('focus', handleFocus);
    };
  }, [componentStyles]);

  // Track page visibility/focus for buttons with "Listen for visibility change" (disabled by default, enabled when !document.hidden or window focus)
  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsPageVisibleOrFocused(!document.hidden);
      if (!document.hidden && reEnableOnVisibilityChange) {
        setDisabledDueToValidationButtonId(null);
        setReEnableOnVisibilityChange(false);
      }
    };
    const handleFocus = () => {
      setIsPageVisibleOrFocused(true);
      if (reEnableOnVisibilityChange) {
        setDisabledDueToValidationButtonId(null);
        setReEnableOnVisibilityChange(false);
      }
    };
    // Enable only on visibilitychange (!document.hidden) or focus, not on mount (disabled by default)
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [reEnableOnVisibilityChange]);

  // Calculate current step index from activeStepId (for builder mode)
  useEffect(() => {
    if (activeStepId !== undefined && activeStepId !== null && steps && steps.length > 0) {
      const sortedSteps = [...steps].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      const stepIndex = sortedSteps.findIndex((step, index) => {
        if (step.id) {
          return step.id === activeStepId;
        }
        return `temp_${index}` === activeStepId || index === activeStepId;
      });
      if (stepIndex >= 0 && stepIndex !== currentStepIndex) {
        setCurrentStepIndex(stepIndex);
      }
    }
  }, [activeStepId, steps, currentStepIndex]);

  const getFieldName = (element: FormElement, index: number): string => {
    return element.name || `field_${index}`;
  };

  // Resolve a field name to the element that owns it (top-level input/select or virtual element for inputs_group child)
  const getElementForFieldName = (fieldName: string): { element: FormElement; fieldName: string } | null => {
    const topLevel = elements.find((el) => (el.type === 'input' || el.type === 'select') && el.name === fieldName);
    if (topLevel) {
      const idx = elements.findIndex((el) => el === topLevel);
      return { element: topLevel, fieldName: getFieldName(topLevel, idx >= 0 ? idx : 0) };
    }
    for (const el of elements) {
      if (el.type !== 'inputs_group' || !el.styles?.inputsGroupInputs) continue;
      const inputItem = (el.styles.inputsGroupInputs as { name?: string; label?: string; placeholder?: string; value?: string; inputType?: string; validationIds?: number[] }[]).find((inp) => inp.name === fieldName);
      if (inputItem) {
        const virtualElement: FormElement = {
          type: 'input',
          name: inputItem.name || fieldName,
          label: inputItem.label,
          placeholder: inputItem.placeholder,
          value: inputItem.value,
          styles: { inputType: (inputItem.inputType as 'text') || 'text' },
          validations: el.validations?.filter((v) => inputItem.validationIds?.includes(v.id)),
        };
        return { element: virtualElement, fieldName };
      }
    }
    return null;
  };

  const validateField = (element: FormElement, value: string): string[] => {
    const errors: string[] = [];
    if (!element.validations || element.validations.length === 0) {
      return errors;
    }

    element.validations.forEach((validation) => {
      switch (validation.type) {
        case 'required': {
          if (!value || value.trim() === '') {
            errors.push(validation.message || `${element.label || element.name || 'Field'} is required`);
          }
          break;
        }
        case 'min_length': {
          const minLength = validation.config?.minLength || validation.config?.min || 0;
          if (value && value.length < minLength) {
            errors.push(validation.message || `Minimum length is ${minLength} characters`);
          }
          break;
        }
        case 'max_length': {
          const maxLength = validation.config?.maxLength || validation.config?.max || Infinity;
          if (value && value.length > maxLength) {
            errors.push(validation.message || `Maximum length is ${maxLength} characters`);
          }
          break;
        }
        case 'email': {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (value && !emailRegex.test(value)) {
            errors.push(validation.message || 'Please enter a valid email address');
          }
          break;
        }
        case 'url': {
          try {
            new URL(value);
          } catch {
            if (value) {
              errors.push(validation.message || 'Please enter a valid URL');
            }
          }
          break;
        }
        case 'pattern': {
          if (validation.config?.pattern) {
            const regex = new RegExp(validation.config.pattern);
            if (value && !regex.test(value)) {
              errors.push(validation.message || 'Invalid format');
            }
          }
          break;
        }
        case 'min': {
          const min = validation.config?.min;
          if (min !== undefined && value && parseFloat(value) < min) {
            errors.push(validation.message || `Minimum value is ${min}`);
          }
          break;
        }
        case 'max': {
          const max = validation.config?.max;
          if (max !== undefined && value && parseFloat(value) > max) {
            errors.push(validation.message || `Maximum value is ${max}`);
          }
          break;
        }
        case 'custom': {
          const isValid = runCustomValidation(validation.config?.functionCode, value);
          if (!isValid) {
            errors.push(validation.message || 'Invalid value');
          }
          break;
        }
      }
    });

    return errors;
  };

  const handleInputChange = (element: FormElement, value: string, index: number) => {
    const fieldName = getFieldName(element, index);
    setFormValues((prev) => {
      const newValues = { ...prev, [fieldName]: value };
      
      // Validate immediately with the new value
      const triggers = element.styles?.validationTriggers || [];
      if (triggers.includes('onChange') || triggers.includes('onInput')) {
        const errors = validateField(element, value);
        setValidationErrors((prevErrors) => ({
          ...prevErrors,
          [fieldName]: errors,
        }));
      }
      
      return newValues;
    });
  };

  const handleInputBlur = (element: FormElement, index: number) => {
    const fieldName = getFieldName(element, index);
    setFormValues((prev) => {
      const value = prev[fieldName] || '';
      const triggers = element.styles?.validationTriggers || [];
      
      if (triggers.includes('onBlur')) {
        const errors = validateField(element, value);
        setValidationErrors((prevErrors) => ({
          ...prevErrors,
          [fieldName]: errors,
        }));
      }
      
      return prev;
    });
  };

  const handleButtonClick = async (buttonElement: FormElement) => {
    // If this button is the "related button" for an inputs_group: validate current input, then advance only if valid; do not return so step nav still runs
    const group = elements.find((el) => el.type === 'inputs_group' && el.id != null && el.styles?.inputsGroupButtonId != null && Number(el.styles.inputsGroupButtonId) === Number(buttonElement.id));
    if (group && group.id != null) {
      const groupId = Number(group.id);
      const currentIndex = inputsGroupCurrentIndex[groupId] ?? 0;
      const inputs = (group.styles?.inputsGroupInputs || []) as { name?: string; label?: string; placeholder?: string; value?: string; inputType?: string; validationIds?: number[] }[];
      const currentInput = inputs[currentIndex];
      const fieldName = currentInput?.name || `inputs_group_${groupId}_${currentIndex}`;
      const virtualElement: FormElement | null = currentInput ? {
        type: 'input',
        name: currentInput.name || fieldName,
        label: currentInput.label,
        placeholder: currentInput.placeholder,
        value: currentInput.value,
        styles: { inputType: (currentInput.inputType as 'text') || 'text' },
        validations: group.validations?.filter((v) => currentInput.validationIds?.includes(v.id)),
      } : null;
      const value = formValues[fieldName] ?? currentInput?.value ?? '';
      const validationErrorsForCurrent = virtualElement ? validateField(virtualElement, value) : [];
      if (validationErrorsForCurrent.length > 0) {
        setValidationErrors((prev) => ({ ...prev, [fieldName]: validationErrorsForCurrent }));
        const message = validationErrorsForCurrent[0] || 'Please fix the errors before continuing.';
        toast.error(message);
        return;
      }
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next[fieldName];
        return next;
      });
      if (currentIndex < inputs.length - 1 && inputs.length > 0) {
        const duration = group.styles?.inputsGroupAnimationDuration ?? 300;
        const useAnimation = group.styles?.inputsGroupAnimation === 'fade';
        if (useAnimation && duration > 0) {
          setInputsGroupVisible((prev) => ({ ...prev, [groupId]: false }));
          setTimeout(() => {
            setInputsGroupCurrentIndex((prev) => ({ ...prev, [groupId]: currentIndex + 1 }));
            setInputsGroupVisible((prev) => ({ ...prev, [groupId]: true }));
          }, duration);
        } else {
          setInputsGroupCurrentIndex((prev) => ({ ...prev, [groupId]: currentIndex + 1 }));
        }
      }
      // Do not return here so step navigation still runs when this button has showStep
    }

    const triggerFor = buttonElement.styles?.triggerValidationsFor || [];
    const showStep = buttonElement.styles?.showStep;
    const newErrors: ValidationErrors = { ...validationErrors };
    let hasErrors = false;

    // Validate fields if button triggers validations (including fields inside inputs_group)
    triggerFor.forEach((inputName) => {
      const resolved = getElementForFieldName(inputName);
      if (resolved) {
        const { element: inputElement, fieldName } = resolved;
        const value = formValues[fieldName] || '';
        const errors = validateField(inputElement, value);
        if (errors.length > 0) {
          newErrors[fieldName] = errors;
          hasErrors = true;
        } else {
          delete newErrors[fieldName];
        }
      }
    });

    setValidationErrors(newErrors);

    // If validation errors exist: show error as toast popup, disable button
    if (hasErrors) {
      const firstError = Object.values(newErrors).flat()[0] || 'Validation failed';
      const message = buttonElement.styles?.disabledClickMessage?.trim() || firstError;
      toast.error(message);
      setDisabledDueToValidationButtonId(buttonElement.id != null ? Number(buttonElement.id) : null);
      setReEnableOnVisibilityChange(buttonElement.styles?.listenVisibilityChange === true);
      return;
    }

    setDisabledDueToValidationButtonId(null);
    setReEnableOnVisibilityChange(false);

    // If step-by-step mode and no validation errors, navigate to target step
    if (viewMode === 'step_by_step' && showStep !== undefined && steps && steps.length > 0) {
      const targetStepIndex = steps.findIndex((step) => step.id === showStep);
      if (targetStepIndex >= 0 && targetStepIndex !== currentStepIndex) {
        navigateToStep(targetStepIndex);
      }
    }
  };

  const navigateToStep = (stepIndex: number) => {
    if (isTransitioning || stepIndex === currentStepIndex) return;
    
    setIsTransitioning(true);
    
    const animationDuration = stepAnimation === 'none' ? 0 : 300;
    
    setTimeout(() => {
      setCurrentStepIndex(stepIndex);
      setIsTransitioning(false);
    }, animationDuration);
  };

  // When disabled button is clicked: run validation, set errors, scroll to first invalid field, show red popup
  const handleDisabledButtonClick = (buttonElement: FormElement) => {
    const triggerFor = buttonElement.styles?.triggerValidationsFor || [];
    const newErrors: ValidationErrors = { ...validationErrors };
    let firstInvalidFieldName: string | null = null;

    triggerFor.forEach((inputName) => {
      const resolved = getElementForFieldName(inputName);
      if (resolved) {
        const { element: inputElement, fieldName } = resolved;
        const value = formValues[fieldName] ?? '';
        const errors = validateField(inputElement, value);
        if (errors.length > 0) {
          newErrors[fieldName] = errors;
          if (firstInvalidFieldName == null) firstInvalidFieldName = fieldName;
        } else {
          delete newErrors[fieldName];
        }
      }
    });

    setValidationErrors(newErrors);

    const firstError = Object.values(newErrors).flat()[0] || 'Validation failed';
    const message = buttonElement.styles?.disabledClickMessage?.trim() || firstError;
    toast.error(message);

    if (firstInvalidFieldName) {
      const scrollTargetId = `field-${firstInvalidFieldName}`;
      setTimeout(() => {
        document.getElementById(scrollTargetId)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  };

  const renderElement = (element: FormElement, index: number) => {
    // Helper to combine desktop, mobile, and hover styles into MUI sx format
    const combineStyles = (
      desktop: Record<string, string | number>,
      mobile: Record<string, string | number>,
      hover: Record<string, string | number>,
      isButton = false
    ): any => {
      const hasMobile = Object.keys(mobile).length > 0;
      const hasHover = isButton && Object.keys(hover).length > 0;
      
      if (!hasMobile && !hasHover) {
        return desktop;
      }
      
      const sx: any = { ...desktop };
      
      if (hasMobile) {
        sx['@media (max-width: 768px)'] = mobile;
      }
      
      if (hasHover) {
        sx['&:hover'] = hover;
      }
      
      return sx;
    };

    // Helper to separate text styles from container styles
    const getTextStyles = (allStyles: Record<string, string | number>): Record<string, string | number> => {
      const textStyleKeys = ['color', 'fontSize', 'fontWeight', 'fontFamily', 'lineHeight', 'textAlign', 'textDecoration'];
      const textStyles: Record<string, string | number> = {};
      textStyleKeys.forEach(key => {
        if (allStyles[key] !== undefined && allStyles[key] !== null && allStyles[key] !== '') {
          textStyles[key] = allStyles[key];
        }
      });
      return textStyles;
    };

    const getContainerStyles = (allStyles: Record<string, string | number>): Record<string, string | number> => {
      const textStyleKeys = ['color', 'fontSize', 'fontWeight', 'fontFamily', 'lineHeight', 'textAlign', 'textDecoration'];
      const componentSpecificDisplayKeys = [
        'textWithValueTitleColor', 'textWithValueValueColor',
        'processingStatusProcessingTitleColor', 'processingStatusStatusTitleColor',
        'linkAlign',
      ];
      const containerStyles: Record<string, string | number> = {};
      Object.entries(allStyles).forEach(([key, value]) => {
        if (
          !textStyleKeys.includes(key) &&
          !componentSpecificDisplayKeys.includes(key) &&
          value !== undefined && value !== null && value !== ''
        ) {
          containerStyles[key] = value;
        }
      });
      return containerStyles;
    };
    
    // Helper to combine text/container styles with mobile/hover support
    const getCombinedTextStyles = (
      desktop: Record<string, string | number>,
      mobile: Record<string, string | number>,
      hover: Record<string, string | number>,
      isButton = false
    ) => {
      const desktopText = getTextStyles(desktop);
      const mobileText = getTextStyles(mobile);
      const hoverText = getTextStyles(hover);
      return combineStyles(desktopText, mobileText, hoverText, isButton);
    };
    
    const getCombinedContainerStyles = (
      desktop: Record<string, string | number>,
      mobile: Record<string, string | number>,
      hover: Record<string, string | number>,
      isButton = false
    ) => {
      const desktopContainer = getContainerStyles(desktop);
      const mobileContainer = getContainerStyles(mobile);
      const hoverContainer = getContainerStyles(hover);
      return combineStyles(desktopContainer, mobileContainer, hoverContainer, isButton);
    };

    // Helper to get common HTML attributes
    const getHtmlAttributes = () => ({
      id: element.htmlId || undefined,
      className: element.className || undefined,
    });

    // Extract CSS-compatible styles from element.styles, excluding non-CSS properties
    // Also merge styles from component style if componentStyleId is present
    // Returns desktop, mobile, and hover styles separately
    const getElementStyles = (): {
      desktop: Record<string, string | number>;
      mobile: Record<string, string | number>;
      hover: Record<string, string | number>;
    } => {
      if (!element.styles) return { desktop: {}, mobile: {}, hover: {} };
      
      const nonCssProperties = [
        'validationTriggers',
        'triggerValidationsFor',
        'showStep',
        'componentStyleId',
        'listItems',
        'subtitle',
        'textValue',
        'textWithValueValueFormat',
        'initialTime',
        'copyIcon',
        'copyDoneIcon',
        'copySuccessMessage',
        'copySubtitle',
        'linkIcon',
        'uploadSubtitle',
        'hideLabels',
        'modalElements',
        'notificationIcon',
        'notificationDescription',
        'notificationDescriptionType',
        'notificationListItems',
        'containerElements',
        'inputType',
        'selectOptions',
        'listenVisibilityChange',
        'disabledClickMessage',
        'inputsGroupInputs',
        'inputsGroupButtonId',
        'inputsGroupAnimation',
        'inputsGroupAnimationDuration',
      ];

      // Start with styles from component style if componentStyleId exists
      const componentStyleId = element.styles?.componentStyleId;
      const componentStyle = componentStyleId && loadedComponentStyles.length > 0
        ? loadedComponentStyles.find((s: ComponentStyle) => s.id === componentStyleId)
        : null;
      
      // If componentStyleId exists, ONLY use styles from component_styles table
      // Don't use any CSS properties from element.styles (they should not be there)
      if (componentStyleId && componentStyle && componentStyle.styles) {
        const desktopStyles: Record<string, string | number> = {};
        const mobileStyles: Record<string, string | number> = {};
        const hoverStyles: Record<string, string | number> = {};
        
        Object.entries(componentStyle.styles).forEach(([key, value]) => {
          if (value !== null && value !== undefined && value !== '') {
            if (key.startsWith('mobile_')) {
              mobileStyles[key.replace('mobile_', '')] = value;
            } else if (key.startsWith('hover_')) {
              hoverStyles[key.replace('hover_', '')] = value;
            } else {
              desktopStyles[key] = value;
            }
          }
        });
        
        return { desktop: desktopStyles, mobile: mobileStyles, hover: hoverStyles };
      }

      // If no componentStyleId, use element's own styles (backward compatibility)
      const desktopStyles: Record<string, string | number> = {};
      const mobileStyles: Record<string, string | number> = {};
      const hoverStyles: Record<string, string | number> = {};
      
      Object.entries(element.styles || {}).forEach(([key, value]) => {
        if (!nonCssProperties.includes(key) && value !== null && value !== undefined && value !== '') {
          if (key.startsWith('mobile_')) {
            mobileStyles[key.replace('mobile_', '')] = value as string | number;
          } else if (key.startsWith('hover_')) {
            hoverStyles[key.replace('hover_', '')] = value as string | number;
          } else {
            desktopStyles[key] = value as string | number;
          }
        }
      });

      return { desktop: desktopStyles, mobile: mobileStyles, hover: hoverStyles };
    };

    switch (element.type) {
      case 'input': {
        const fieldName = element.name || `field_${index}`;
        const fieldErrors = validationErrors[fieldName] || [];
        return (
          <Box key={index} sx={{ mb: 2 }} id={element.htmlId || `field-${fieldName}`} className={element.className || undefined}>
            {element.label && (
              <Typography level="body-sm" sx={{ mb: 0.5, fontWeight: 'md' }}>
                {element.label}
              </Typography>
            )}
            <Input
              placeholder={element.placeholder || ''}
              name={fieldName}
              value={formValues[fieldName] || ''}
              onChange={(e) => handleInputChange(element, e.target.value, index)}
              onBlur={() => handleInputBlur(element, index)}
              sx={{
                width: '100%',
                '&.Mui-focused': {
                  borderColor: fieldErrors.length > 0 ? 'danger.500' : 'primary.500',
                },
              }}
              color={fieldErrors.length > 0 ? 'danger' : 'neutral'}
              variant="outlined"
              error={fieldErrors.length > 0}
            />
            {fieldErrors.length > 0 && (
              <Box sx={{ mt: 1 }}>
                {fieldErrors.map((error, errorIndex) => (
                  <Alert
                    key={errorIndex}
                    color="danger"
                    size="sm"
                    startDecorator={<ErrorOutline />}
                    sx={{
                      mt: errorIndex > 0 ? 0.5 : 0,
                      fontSize: '0.875rem',
                      fontWeight: 500,
                    }}
                  >
                    {error}
                  </Alert>
                ))}
              </Box>
            )}
          </Box>
        );
      }
      case 'select': {
        const fieldName = element.name || `field_${index}`;
        const fieldErrors = validationErrors[fieldName] || [];
        const options = element.styles?.selectOptions || [];
        const currentValue = formValues[fieldName] !== undefined ? formValues[fieldName] : (options[0]?.value ?? '');
        return (
          <Box key={index} sx={{ mb: 2 }} id={element.htmlId || `field-${fieldName}`} className={element.className || undefined}>
            {element.label && (
              <Typography level="body-sm" sx={{ mb: 0.5, fontWeight: 'md' }}>
                {element.label}
              </Typography>
            )}
            <Select
              value={currentValue}
              onChange={(_event, value) => handleInputChange(element, value ?? '', index)}
              sx={{
                width: '100%',
                '&.Mui-focused': {
                  borderColor: fieldErrors.length > 0 ? 'danger.500' : 'primary.500',
                },
              }}
              color={fieldErrors.length > 0 ? 'danger' : 'neutral'}
              variant="outlined"
            >
              <Option value="">— Select —</Option>
              {options.map((opt, i) => (
                <Option key={i} value={opt.value}>
                  {opt.label || opt.value}
                </Option>
              ))}
            </Select>
            {fieldErrors.length > 0 && (
              <Box sx={{ mt: 1 }}>
                {fieldErrors.map((error, errorIndex) => (
                  <Alert
                    key={errorIndex}
                    color="danger"
                    size="sm"
                    startDecorator={<ErrorOutline />}
                    sx={{
                      mt: errorIndex > 0 ? 0.5 : 0,
                      fontSize: '0.875rem',
                      fontWeight: 500,
                    }}
                  >
                    {error}
                  </Alert>
                ))}
              </Box>
            )}
          </Box>
        );
      }
      case 'inputs_group': {
        const inputs = element.styles?.inputsGroupInputs || [];
        const groupId = element.id != null ? Number(element.id) : 0;
        const currentIndex = Math.min(inputsGroupCurrentIndex[groupId] ?? 0, Math.max(0, inputs.length - 1));
        const visible = inputsGroupVisible[groupId] !== false;
        const duration = element.styles?.inputsGroupAnimationDuration ?? 300;
        const useAnimation = element.styles?.inputsGroupAnimation === 'fade';

        if (inputs.length === 0) {
          return (
            <Box key={index} sx={{ mb: 2 }} {...getHtmlAttributes()}>
              <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>No inputs in group</Typography>
            </Box>
          );
        }

        const currentInput = inputs[currentIndex];
        const fieldName = currentInput.name || `inputs_group_${groupId}_${currentIndex}`;
        const fieldErrors = validationErrors[fieldName] || [];
        const currentValue = formValues[fieldName] !== undefined ? formValues[fieldName] : (currentInput.value ?? '');
        const virtualElement: FormElement = {
          type: 'input',
          name: currentInput.name || fieldName,
          label: currentInput.label,
          placeholder: currentInput.placeholder,
          value: currentInput.value,
          styles: { inputType: (currentInput.inputType as 'text') || 'text' },
          validations: element.validations?.filter((v) => currentInput.validationIds?.includes(v.id)),
        };

        return (
          <Box
            key={index}
            sx={{
              mb: 2,
              opacity: visible ? 1 : 0,
              transition: useAnimation ? `opacity ${duration}ms ease-in-out` : 'none',
            }}
            {...getHtmlAttributes()}
          >
            {currentInput.label && (
              <Typography level="body-sm" sx={{ mb: 0.5, fontWeight: 'md' }}>
                {currentInput.label}
              </Typography>
            )}
            <Input
              type={(currentInput.inputType as 'text') || 'text'}
              placeholder={currentInput.placeholder || ''}
              name={fieldName}
              value={currentValue}
              onChange={(e) => handleInputChange(virtualElement, e.target.value, 0)}
              onBlur={() => handleInputBlur(virtualElement, 0)}
              sx={{
                width: '100%',
                '&.Mui-focused': {
                  borderColor: fieldErrors.length > 0 ? 'danger.500' : 'primary.500',
                },
              }}
              color={fieldErrors.length > 0 ? 'danger' : 'neutral'}
              variant="outlined"
              error={fieldErrors.length > 0}
            />
            {fieldErrors.length > 0 && (
              <Box sx={{ mt: 1 }}>
                {fieldErrors.map((err, errIdx) => (
                  <Alert
                    key={errIdx}
                    color="danger"
                    size="sm"
                    startDecorator={<ErrorOutline />}
                    sx={{ mt: errIdx > 0 ? 0.5 : 0, fontSize: '0.875rem', fontWeight: 500 }}
                  >
                    {err}
                  </Alert>
                ))}
              </Box>
            )}
          </Box>
        );
      }
      case 'button': {
        const { desktop, mobile, hover } = getElementStyles();
        const textStyles = getCombinedTextStyles(desktop, mobile, hover, true);
        const containerStyles = getCombinedContainerStyles(desktop, mobile, hover, true);
        const triggerFor = element.styles?.triggerValidationsFor || [];
        const hasValidations = triggerFor.length > 0;
        let triggeredFieldsPass = true;
        if (hasValidations) {
          for (const inputName of triggerFor) {
            const resolved = getElementForFieldName(inputName);
            if (resolved) {
              const { element: inputElement, fieldName } = resolved;
              const value = formValues[fieldName] ?? '';
              const errors = validateField(inputElement, value);
              if (errors.length > 0) {
                triggeredFieldsPass = false;
                break;
              }
            }
          }
        }
        const isDisabledDueToClickFailure = hasValidations && element.id != null && disabledDueToValidationButtonId === Number(element.id);
        const listenVisibilityChange = element.styles?.listenVisibilityChange === true;
        const disabledByVisibility = listenVisibilityChange && !isPageVisibleOrFocused;
        const isDisabled = (hasValidations && (isDisabledDueToClickFailure || !triggeredFieldsPass)) || disabledByVisibility;
        return (
          <Box
            key={index}
            {...getHtmlAttributes()}
            sx={{ mb: 2, ...(isDisabled && { cursor: 'pointer' }) }}
            onClick={(e) => {
              if (isDisabled) {
                e.preventDefault();
                if (disabledByVisibility && !(hasValidations && (isDisabledDueToClickFailure || !triggeredFieldsPass))) {
                  const visibilityMessage = element.styles?.disabledClickMessage?.trim() || 'Please return to this tab or focus the window to enable this button.';
                  toast.error(visibilityMessage);
                  return;
                }
                handleDisabledButtonClick(element);
              }
            }}
          >
            <Button
              variant="solid"
              color="primary"
              disabled={isDisabled}
              sx={{
                width: '100%',
                ...containerStyles,
                ...textStyles,
                ...(isDisabled && {
                  opacity: 0.65,
                  cursor: 'pointer',
                  pointerEvents: 'none',
                  '&.Mui-disabled': {
                    opacity: 0.65,
                  },
                }),
              }}
              onClick={() => handleButtonClick(element)}
            >
              {element.label || 'Button'}
            </Button>
          </Box>
        );
      }
      case 'text': {
        const { desktop, mobile, hover } = getElementStyles();
        const textStyles = getCombinedTextStyles(desktop, mobile, hover);
        const containerStyles = getCombinedContainerStyles(desktop, mobile, hover);
        const rawValue = resolveElementMainValue(element, undefined, 'Sample text');
        const textFormat = element.styles?.textFormat ?? 'none';
        const locale = element.styles?.textNumberLocale || 'de-DE';
        const minFrac = element.styles?.textNumberMinFractionDigits;
        const maxFrac = element.styles?.textNumberMaxFractionDigits;
        const displayValue = textFormat === 'number'
          ? formatFirstNumberInText(rawValue, locale, minFrac, maxFrac)
          : rawValue;
        return (
          <Box key={index} sx={{ mb: 2, ...containerStyles }} {...getHtmlAttributes()}>
            <Typography level="body-md" sx={textStyles}>
              {displayValue}
            </Typography>
          </Box>
        );
      }
      case 'image': {
        const { desktop, mobile, hover } = getElementStyles();
        const textStyles = getCombinedTextStyles(desktop, mobile, hover);
        const containerStyles = getCombinedContainerStyles(desktop, mobile, hover);
        // Extract textAlign for image centering (from desktop styles)
        const imageAlign = getTextStyles(desktop).textAlign || getContainerStyles(desktop).textAlign || 'left';
        return (
          <Box key={index} sx={{ mb: 2, textAlign: imageAlign as any }} {...getHtmlAttributes()}>
            {element.label && (
              <Typography level="body-sm" sx={{ mb: 0.5, fontWeight: 'md', ...textStyles }}>
                {element.label}
              </Typography>
            )}
            <Box
              component="img"
              src={element.value || 'https://via.placeholder.com/300x200?text=Image'}
              alt={element.label || 'Image'}
              sx={{
                maxWidth: '100%',
                height: 'auto',
                borderRadius: 'sm',
                border: '1px solid',
                borderColor: 'divider',
                objectFit: 'contain',
                ...containerStyles,
                // Remove textAlign from containerStyles as it's applied to parent Box
                textAlign: undefined,
              }}
            />
          </Box>
        );
      }
      case 'list': {
        const listItems = element.styles?.listItems || [];
        const subtitle = element.styles?.subtitle || '';
        const { desktop, mobile, hover } = getElementStyles();
        const textStyles = getCombinedTextStyles(desktop, mobile, hover);
        const containerStyles = getCombinedContainerStyles(desktop, mobile, hover);
        return (
          <Box key={index} sx={{ mb: 2, ...containerStyles }} {...getHtmlAttributes()}>
            {element.label && (
              <Typography level="title-md" sx={{ mb: 0.5, fontWeight: 'md' }}>
                {element.label}
              </Typography>
            )}
            {subtitle && (
              <Typography level="body-sm" sx={{ mb: 1.5, color: 'text.secondary' }}>
                {subtitle}
              </Typography>
            )}
            {listItems.length > 0 ? (
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {listItems.map((item, idx) => (
                  <Typography key={idx} component="li" level="body-sm" sx={{ mb: 0.5, ...textStyles }}>
                    {item}
                  </Typography>
                ))}
              </Box>
            ) : (
              <Typography level="body-sm" sx={{ color: 'text.tertiary', fontStyle: 'italic' }}>
                No items in list
              </Typography>
            )}
          </Box>
        );
      }
      case 'text_with_value': {
        const textValue = element.styles?.textValue || '';
        const valueRaw = element.value || '';
        const valueFormat = element.styles?.textWithValueValueFormat ?? 'none';
        const valueLocale = element.styles?.textNumberLocale || 'de-DE';
        const valueMinFrac = element.styles?.textNumberMinFractionDigits;
        const valueMaxFrac = element.styles?.textNumberMaxFractionDigits;
        const value =
          valueFormat === 'phone'
            ? formatPhoneDisplayValue(valueRaw)
            : valueFormat === 'number'
              ? formatFirstNumberInText(valueRaw, valueLocale, valueMinFrac, valueMaxFrac)
              : valueRaw;
        const { desktop, mobile, hover } = getElementStyles();
        const textStyles = getCombinedTextStyles(desktop, mobile, hover);
        const containerStyles = getCombinedContainerStyles(desktop, mobile, hover);
        const titleColor = (desktop as Record<string, string | number>).textWithValueTitleColor;
        const valueColor = (desktop as Record<string, string | number>).textWithValueValueColor;
        return (
          <Box key={index} sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', ...containerStyles }} {...getHtmlAttributes()}>
            <Typography level="body-md" sx={{ flex: 1, ...textStyles, ...(titleColor != null && titleColor !== '' && { color: String(titleColor) }) }}>
              {textValue || 'Text'}
            </Typography>
            <Typography level="body-md" sx={{ fontWeight: 'md', ml: 2, textAlign: 'right', ...textStyles, ...(valueColor != null && valueColor !== '' && { color: String(valueColor) }) }}>
              {value || 'Value'}
            </Typography>
          </Box>
        );
      }
      case 'timer': {
        const timerPosition = element.styles?.timerPosition ?? 'inline';
        if (timerPosition === 'top-left' || timerPosition === 'top' || timerPosition === 'top-right') {
          return null;
        }
        const onExpiry = element.styles?.timerOnExpiry ?? 'show_00';
        const linkElementIdAfterExpiry = element.styles?.linkElementIdAfterExpiry;
        const showLinkAfterExpiry = onExpiry === 'show_link' && linkElementIdAfterExpiry && revealedLinkIds.has(Number(linkElementIdAfterExpiry));
        if (showLinkAfterExpiry) {
          const linkEl = getElementById(Number(linkElementIdAfterExpiry));
          if (linkEl?.type === 'link' && linkEl.styles?.linkHidden) {
            return null;
          }
          if (linkEl?.type === 'link') {
            const linkUrl = linkEl.value || '#';
            const linkTitle = linkEl.label || 'Link';
            const linkIcon = linkEl.styles?.linkIcon || '';
            const hideLinkText = linkEl.styles?.hideLinkText || false;
            const linkTarget = linkEl.styles?.linkTarget ?? '_blank';
            const { desktop: linkDesktop, mobile: linkMobile, hover: linkHover } = getElementStylesWithVariants(linkEl, loadedComponentStyles);
            const linkAlign = (linkDesktop.linkAlign as 'left' | 'center' | 'right') ?? linkEl.styles?.linkAlign ?? 'left';
            const textStyles = getCombinedTextStyles(linkDesktop, linkMobile, linkHover);
            const containerStyles = getCombinedContainerStyles(linkDesktop, linkMobile, linkHover);
            const justifyContent = linkAlign === 'center' ? 'center' : linkAlign === 'right' ? 'flex-end' : 'flex-start';
            return (
              <Box key={index} sx={{ mb: 2, display: 'flex', justifyContent }}>
                <Box
                  component="a"
                  href={linkUrl}
                  target={linkTarget || '_blank'}
                  rel={linkTarget === '_blank' ? 'noopener noreferrer' : undefined}
                  onClick={(e) => e.preventDefault()}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    textDecoration: 'none',
                    ...containerStyles,
                    ...textStyles,
                    color: (textStyles as Record<string, unknown>)?.color ?? 'primary.500',
                  }}
                >
                  {linkIcon && (
                    <Box sx={{ width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }} dangerouslySetInnerHTML={{ __html: sanitizeSvg(linkIcon) }} />
                  )}
                  {!hideLinkText && (
                    <Typography component="span" level="body-md" sx={{ color: 'inherit' }}>
                      {linkTitle}
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          }
          return null;
        }
        return (
          <TimerElement
            key={index}
            element={element}
            componentStyles={loadedComponentStyles}
            onExpiryShowLink={
              onExpiry === 'show_link' && linkElementIdAfterExpiry
                ? (linkId) => setRevealedLinkIds((prev) => new Set(prev).add(linkId))
                : undefined
            }
          />
        );
      }
      case 'svg': {
        const svgCode = element.styles?.svgCode || '';
        const svgAlign = element.styles?.svgAlign || 'left';
        const { desktop, mobile, hover } = getElementStyles();
        const containerStyles = getCombinedContainerStyles(desktop, mobile, hover);
        return (
          <Box
            key={index}
            sx={{
              mb: 2,
              display: 'flex',
              justifyContent: svgAlign === 'center' ? 'center' : svgAlign === 'right' ? 'flex-end' : 'flex-start',
              '& svg': { display: 'block', maxWidth: '100%', height: 'auto' },
              ...containerStyles,
            }}
            {...getHtmlAttributes()}
          >
            {svgCode ? <Box component="span" dangerouslySetInnerHTML={{ __html: sanitizeSvg(svgCode) }} /> : <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>SVG</Typography>}
          </Box>
        );
      }
      case 'qr_code': {
        return <QrCodeFormElement key={index} element={element} componentStyles={loadedComponentStyles} />;
      }
      case 'copy_section': {
        return <CopySectionElement key={index} element={element} componentStyles={loadedComponentStyles} />;
      }
      case 'link': {
        if (element.styles?.linkHidden) {
          const linkId = element.id != null ? Number(element.id) : NaN;
          if (!Number.isFinite(linkId) || !revealedLinkIds.has(linkId)) {
            return (
              <Box key={index} sx={{ mb: 2, py: 1, px: 2, bgcolor: 'background.level2', borderRadius: 'sm' }}>
                <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>Hidden link (shown after timer expires)</Typography>
              </Box>
            );
          }
        }
        const linkUrl = sanitizeUrl(element.value || '#', '#');
        const linkTitle = element.label || 'Link';
        const linkIcon = sanitizeSvg(element.styles?.linkIcon || '');
        const hideLinkText = element.styles?.hideLinkText || false;
        const linkTarget = element.styles?.linkTarget ?? '_blank';
        const { desktop, mobile, hover } = getElementStyles();
        const linkAlign = (desktop.linkAlign as 'left' | 'center' | 'right') ?? element.styles?.linkAlign ?? 'left';
        const textStyles = getCombinedTextStyles(desktop, mobile, hover);
        const containerStyles = getCombinedContainerStyles(desktop, mobile, hover);
        const linkHoverStyles = typeof (textStyles as Record<string, unknown>)?.['&:hover'] === 'object' ? (textStyles as Record<string, unknown>)['&:hover'] as Record<string, unknown> : {};
        const justifyContent = linkAlign === 'center' ? 'center' : linkAlign === 'right' ? 'flex-end' : 'flex-start';
        return (
          <Box key={index} sx={{ mb: 2, display: 'flex', justifyContent }} {...getHtmlAttributes()}>
            <Box
              component="a"
              href={linkUrl}
              target={linkTarget || '_blank'}
              rel={linkTarget === '_blank' ? 'noopener noreferrer' : undefined}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                textDecoration: 'none',
                ...containerStyles,
                ...textStyles,
                color: (textStyles as Record<string, unknown>)?.color ?? 'primary.500',
                '&:hover': {
                  ...linkHoverStyles,
                  textDecoration: 'underline',
                },
              }}
            >
              {linkIcon && (
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                  dangerouslySetInnerHTML={{ __html: sanitizeSvg(linkIcon) }}
                />
              )}
              {!hideLinkText && (
                <Typography component="span" level="body-md" sx={{ color: 'inherit' }}>
                  {linkTitle}
                </Typography>
              )}
            </Box>
          </Box>
        );
      }
      case 'upload_file': {
        return <UploadFileElement key={index} element={element} componentStyles={loadedComponentStyles} />;
      }
      case 'modal': {
        const ModalElement: React.FC<{ element: FormElement }> = ({ element }) => {
          const [open, setOpen] = useState(false);
          const modalElements = element.styles?.modalElements || [];
          const { desktop, mobile, hover } = getElementStyles();
          const textStyles = getCombinedTextStyles(desktop, mobile, hover);
          const containerStyles = getCombinedContainerStyles(desktop, mobile, hover);

          return (
            <Box {...getHtmlAttributes()}>
              <Button
                variant="outlined"
                onClick={() => setOpen(true)}
                sx={{ 
                  mb: 2, 
                  ...containerStyles,
                  ...textStyles,
                }}
              >
                {element.label || 'Open Modal'}
              </Button>
              <Modal open={open} onClose={() => setOpen(false)}>
                <ModalDialog sx={{ maxWidth: 600, width: '90%' }}>
                  <ModalClose />
                  <Typography level="title-lg" sx={{ mb: 2 }}>
                    {element.label || 'Modal'}
                  </Typography>
                  <Stack spacing={0}>
                    {modalElements.length === 0 ? (
                      <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                        No elements in modal
                      </Typography>
                    ) : (
                      modalElements.map((modalEl, modalIdx) => {
                        const modalElementKey = modalEl.id || `modal_element_${modalIdx}`;
                        return (
                          <Box key={modalElementKey}>
                            {renderElement(modalEl, modalIdx)}
                          </Box>
                        );
                      })
                    )}
                  </Stack>
                </ModalDialog>
              </Modal>
            </Box>
          );
        };

        return <ModalElement key={index} element={element} />;
      }
      case 'notification_message': {
        const notificationIcon = element.styles?.notificationIcon || '';
        const notificationDescription = element.styles?.notificationDescription || '';
        const notificationDescriptionType = element.styles?.notificationDescriptionType || 'text';
        const notificationListItems = element.styles?.notificationListItems || [];
        const hasDescription = notificationDescriptionType === 'text' 
          ? notificationDescription 
          : notificationListItems.length > 0;
        const { desktop, mobile, hover } = getElementStyles();
        const textStyles = getCombinedTextStyles(desktop, mobile, hover);
        const containerStyles = getCombinedContainerStyles(desktop, mobile, hover);
        
        return (
          <Box key={index} {...getHtmlAttributes()}>
            <Box
              sx={{
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 'sm',
                bgcolor: 'background.level1',
                ...containerStyles,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: hasDescription ? 1 : 0 }}>
                {notificationIcon && (
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                    dangerouslySetInnerHTML={{ __html: sanitizeSvg(notificationIcon) }}
                  />
                )}
                <Typography level="title-sm" sx={{ fontWeight: 'md', ...textStyles }}>
                  {element.label || 'Notification Title'}
                </Typography>
              </Box>
              {notificationDescriptionType === 'text' && notificationDescription && (
                <Typography level="body-sm" sx={{ color: 'text.secondary', ...textStyles }}>
                  {notificationDescription}
                </Typography>
              )}
              {notificationDescriptionType === 'list' && notificationListItems.length > 0 && (
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {notificationListItems.map((item, idx) => (
                    <Typography key={idx} component="li" level="body-sm" sx={{ mb: 0.5, color: 'text.secondary', ...textStyles }}>
                      {item}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        );
      }
      case 'processing_status': {
        const processingTitle = element.styles?.processingTitle || 'Payment Status';
        const statusTitle = element.styles?.statusTitle || 'In Progress';
        const svg = element.styles?.processingStatusSvg || '';
        const { desktop, mobile, hover } = getElementStyles();
        const textStyles = getCombinedTextStyles(desktop, mobile, hover);
        const containerStyles = getCombinedContainerStyles(desktop, mobile, hover);
        const processingTitleColor = (desktop as Record<string, string | number>).processingStatusProcessingTitleColor;
        const statusTitleColor = (desktop as Record<string, string | number>).processingStatusStatusTitleColor;
        return (
          <Box key={index} sx={{ mb: 2 }} {...getHtmlAttributes()}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 'sm',
                bgcolor: 'background.level1',
                ...containerStyles,
              }}
            >
              <Typography level="title-sm" sx={{ fontWeight: 'md', ...textStyles, ...(processingTitleColor != null && processingTitleColor !== '' && { color: String(processingTitleColor) }) }}>
                {processingTitle}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {svg && (
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      animation: 'processing-spin 1s linear infinite',
                      '@keyframes processing-spin': {
                        from: { transform: 'rotate(0deg)' },
                        to: { transform: 'rotate(360deg)' },
                      },
                    }}
                    dangerouslySetInnerHTML={{ __html: sanitizeSvg(svg) }}
                  />
                )}
                <Typography level="title-sm" sx={{ fontWeight: 'md', ...textStyles, ...(statusTitleColor != null && statusTitleColor !== '' ? { color: String(statusTitleColor) } : { color: 'warning.500' }) }}>
                  {statusTitle}
                </Typography>
              </Box>
            </Box>
          </Box>
        );
      }
      case 'loader': {
        const color = element.styles?.loaderColor ?? 'rgb(11, 111, 199)';
        const width = element.styles?.loaderWidth ?? '50px';
        const height = element.styles?.loaderHeight ?? '50px';
        const { desktop, mobile, hover } = getElementStyles();
        const containerStyles = getCombinedContainerStyles(desktop, mobile, hover);
        return (
          <Box key={index} sx={{ mb: 2, display: 'flex', justifyContent: 'center', ...containerStyles }} {...getHtmlAttributes()}>
            <Box
              className="loader"
              sx={{
                width,
                height,
                border: '4px solid',
                borderColor: '#e0e0e0',
                borderTopColor: color,
                borderRadius: '50%',
                animation: 'loader-spin 1s linear infinite',
                '@keyframes loader-spin': {
                  from: { transform: 'rotate(0deg)' },
                  to: { transform: 'rotate(360deg)' },
                },
              }}
            />
          </Box>
        );
      }
      case 'slideshow': {
        const { desktop, mobile, hover } = getElementStyles();
        let containerStyles = getCombinedContainerStyles(desktop, mobile, hover);
        containerStyles = { ...containerStyles };
        delete (containerStyles as Record<string, unknown>).slideshowViewportBorder;
        delete (containerStyles as Record<string, unknown>).slideshowViewportPadding;
        delete (containerStyles as Record<string, unknown>).slideshowViewportBackground;
        const viewportStyles: Record<string, any> = {};
        if (desktop && (desktop.slideshowViewportBorder != null || desktop.slideshowViewportPadding != null || desktop.slideshowViewportBackground != null)) {
          if (desktop.slideshowViewportBorder != null) viewportStyles.border = desktop.slideshowViewportBorder;
          if (desktop.slideshowViewportPadding != null) viewportStyles.padding = desktop.slideshowViewportPadding;
          if (desktop.slideshowViewportBackground != null) viewportStyles.bgcolor = desktop.slideshowViewportBackground;
        }
        const buttonBg = element.styles?.slideshowButtonBackground ?? 'rgb(11, 111, 199)';
        const slideId = element.htmlId || `slideshow-${index}-${element.id ?? 0}`;
        return (
          <SlideshowElement
            key={index}
            element={element}
            slideId={slideId}
            containerStyles={containerStyles}
            viewportStyles={viewportStyles}
            buttonBackground={buttonBg}
            htmlAttributes={getHtmlAttributes()}
          />
        );
      }
      case 'container': {
        const containerElements = element.styles?.containerElements || [];
        const { desktop, mobile, hover } = getElementStyles();
        const containerStyles = getCombinedContainerStyles(desktop, mobile, hover);
        return (
          <Box key={index} sx={{ mb: 2 }} {...getHtmlAttributes()}>
            {element.label && (
              <Typography level="body-sm" sx={{ mb: 1, fontWeight: 'md' }}>
                {element.label}
              </Typography>
            )}
            <Box
              sx={{
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 'sm',
                bgcolor: 'background.level1',
                ...containerStyles,
              }}
            >
              {containerElements.length === 0 ? (
                <Typography level="body-sm" sx={{ color: 'text.tertiary', fontStyle: 'italic', textAlign: 'center' }}>
                  No elements in container
                </Typography>
              ) : (
                <Stack spacing={0}>
                  {containerElements.map((containerEl, containerIdx) => {
                    const containerElementKey = containerEl.id || `container_element_${containerIdx}`;
                    return (
                      <Box key={containerElementKey}>
                        {renderElement(containerEl, containerIdx)}
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </Box>
          </Box>
        );
      }
      case 'step_progress_bar': {
        if (!steps || steps.length === 0) {
          const { desktop, mobile, hover } = getElementStyles();
          const containerStyles = getCombinedContainerStyles(desktop, mobile, hover);
          return (
            <Box key={index} sx={{ mb: 2, ...containerStyles }} {...getHtmlAttributes()}>
              <Typography level="body-sm" sx={{ color: 'text.tertiary', textAlign: 'center' }}>
                No steps available
              </Typography>
            </Box>
          );
        }

        const sortedSteps = [...steps].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        const currentStepNumber = currentStepIndex + 1;
        const totalSteps = sortedSteps.length;
        const hideLabels = element.styles?.hideLabels || false;
        const { desktop, mobile, hover } = getElementStyles();
        const containerStyles = getCombinedContainerStyles(desktop, mobile, hover);

        const componentStyleId = element.styles?.componentStyleId;
        const stepBarStyle = componentStyleId && loadedComponentStyles.length > 0
          ? loadedComponentStyles.find((s: ComponentStyle) => s.id === componentStyleId)
          : null;
        const ss = stepBarStyle?.styles || {};
        const activeBg = ss.stepProgressBarActiveBg ?? 'primary.500';
        const completedBg = ss.stepProgressBarCompletedBg ?? 'success.500';
        const inactiveBg = ss.stepProgressBarInactiveBg ?? 'background.level2';
        const circleWidth = ss.stepProgressBarCircleWidth ?? 32;
        const circleHeight = ss.stepProgressBarCircleHeight ?? 32;
        const circleGap = ss.stepProgressBarCircleGap ?? 1;
        const lineHeight = ss.stepProgressBarLineHeight ?? '2px';
        const lineWidth = ss.stepProgressBarLineWidth;
        const completedLineColor = ss.stepProgressBarCompletedLineColor ?? 'success.500';
        const inactiveLineColor = ss.stepProgressBarInactiveLineColor ?? 'background.level2';
        const hideNumbers = ss.stepProgressBarHideNumbers === true || ss.stepProgressBarHideNumbers === 'true';
        const align = ss.stepProgressBarAlign ?? 'left';
        const barAlignSx =
          align === 'center'
            ? { width: 'fit-content' as const, mx: 'auto' }
            : align === 'right'
            ? { width: 'fit-content' as const, ml: 'auto' }
            : {};

        const hiddenStepIndices = (element.styles?.hiddenStepIndices as number[] | undefined) || [];
        const visibleSteps = sortedSteps
          .map((step, idx) => ({ step, originalIndex: idx }))
          .filter(({ originalIndex }) => !hiddenStepIndices.includes(originalIndex));

        if (visibleSteps.length === 0) {
          return (
            <Box key={index} sx={{ mb: 2, ...containerStyles }} {...getHtmlAttributes()}>
              <Typography level="body-sm" sx={{ color: 'text.tertiary', textAlign: 'center' }}>
                No steps to display
              </Typography>
            </Box>
          );
        }

        return (
          <Box key={index} sx={{ mb: 2, ...containerStyles }} {...getHtmlAttributes()}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: circleGap, mb: hideLabels ? 0 : 1, ...barAlignSx }}>
              {visibleSteps.map(({ step, originalIndex }, visibleIdx) => {
                const stepNumber = visibleIdx + 1;
                const isActive = originalIndex === currentStepIndex;
                const isCompleted = originalIndex < currentStepIndex;

                return (
                  <React.Fragment key={originalIndex}>
                    <Box
                      sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: hideLabels ? 0 : 0.5,
                      }}
                    >
                      <Box
                        sx={{
                          width: circleWidth,
                          height: circleHeight,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: isActive ? activeBg : isCompleted ? completedBg : inactiveBg,
                          color: isActive || isCompleted ? 'white' : 'text.secondary',
                          fontWeight: 'md',
                          fontSize: '0.875rem',
                          transition: 'all 0.2s',
                        }}
                      >
                        {hideNumbers ? (isCompleted ? '✓' : '') : (isCompleted ? '✓' : stepNumber)}
                      </Box>
                      {!hideLabels && (
                        <Typography
                          level="body-xs"
                          sx={{
                            textAlign: 'center',
                            color: isActive ? 'primary.600' : 'text.secondary',
                            fontWeight: isActive ? 'md' : 'normal',
                            maxWidth: '100px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {step.title}
                        </Typography>
                      )}
                    </Box>
                    {visibleIdx < visibleSteps.length - 1 && (
                      <Box
                        sx={{
                          flex: 1,
                          height: lineHeight,
                          ...(lineWidth ? { minWidth: lineWidth } : {}),
                          backgroundColor: originalIndex < currentStepIndex ? completedLineColor : inactiveLineColor,
                          transition: 'all 0.2s',
                        }}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </Box>
            {!hideLabels && (
              <Typography level="body-xs" sx={{ textAlign: 'center', color: 'text.tertiary', mt: 1 }}>
                Step {currentStepNumber} of {totalSteps}
              </Typography>
            )}
          </Box>
        );
      }
      default:
        return null;
    }
  };

  return (
    <Card
      variant="outlined"
      sx={{
        position: 'sticky',
        top: 20,
        maxHeight: 'calc(100vh - 40px)',
        overflow: 'auto',
      }}
    >
      <CardContent>
        <Typography level="title-lg" sx={{ mb: 2, textAlign: 'center' }}>
          Form Preview
        </Typography>
        
        <Box
          component="form"
          ref={formRef}
            onSubmit={(e) => {
            e.preventDefault();
            // Validate all fields with onSubmit trigger
            const newErrors: ValidationErrors = {};
            elements.forEach((element, index) => {
              if (element.type === 'input' || element.type === 'select') {
                const triggers = element.styles?.validationTriggers || [];
                if (triggers.includes('onSubmit')) {
                  const fieldName = getFieldName(element, index);
                  const value = formValues[fieldName] || '';
                  const errors = validateField(element, value);
                  if (errors.length > 0) {
                    newErrors[fieldName] = errors;
                  }
                }
              }
            });
            setValidationErrors(newErrors);
          }}
          sx={{
            position: 'relative',
            p: 3,
            bgcolor: 'background.surface',
            borderRadius: 'md',
            border: '1px solid',
            borderColor: 'divider',
            ...getFormStyles(),
          }}
        >
          {(() => {
            const positionedTimers = elements.filter(
              (el) =>
                !el.styles?.hidden &&
                isElementVisible(el, null, { treatMissingTxAsPass: true }) &&
                el.type === 'timer' &&
                (el.styles?.timerPosition === 'top-left' ||
                  el.styles?.timerPosition === 'top' ||
                  el.styles?.timerPosition === 'top-right'),
            );
            if (positionedTimers.length === 0) return null;
            return (
              <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 1, pointerEvents: 'none' }}>
                {positionedTimers.map((el, i) => {
                  const linkId = el.styles?.timerOnExpiry === 'show_link' ? el.styles?.linkElementIdAfterExpiry : undefined;
                  if (linkId && revealedLinkIds.has(Number(linkId))) return null;
                  return (
                    <TimerElement
                      key={el.id ?? `timer-positioned-${i}`}
                      element={el}
                      componentStyles={loadedComponentStyles}
                      onExpiryShowLink={linkId ? (id) => setRevealedLinkIds((prev) => new Set(prev).add(id)) : undefined}
                    />
                  );
                })}
              </Box>
            );
          })()}
          {formName && !shouldHideFormName && (
            <Typography level="h3" sx={{ mb: 1, textAlign: 'center' }}>
              {formName}
            </Typography>
          )}
          
          {formDescription && (
            <Typography level="body-md" sx={{ mb: 3, textAlign: 'center', color: 'text.secondary' }}>
              {formDescription}
            </Typography>
          )}

          {(() => {
            if (steps && steps.length > 0) {
              // Render with steps
              const sortedSteps = [...steps].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
              
              if (sortedSteps.length === 0) {
                return (
                  <Box
                    sx={{
                      py: 4,
                      textAlign: 'center',
                      color: 'text.tertiary',
                    }}
                  >
                    <Typography level="body-sm">
                      Add steps to see preview
                    </Typography>
                  </Box>
                );
              }

              // Step-by-step mode: show only current step
              // If activeStepId is provided (builder mode), use that to determine which step to show
              if (viewMode === 'step_by_step' || (activeStepId !== undefined && activeStepId !== null)) {
                // In builder mode with activeStepId, find the step matching activeStepId
                let stepToShow = sortedSteps[currentStepIndex];
                if (activeStepId !== undefined && activeStepId !== null) {
                  const activeStepIndex = sortedSteps.findIndex((step, index) => {
                    if (step.id) {
                      return step.id === activeStepId;
                    }
                    return `temp_${index}` === activeStepId || index === activeStepId;
                  });
                  if (activeStepIndex >= 0) {
                    stepToShow = sortedSteps[activeStepIndex];
                  }
                }
                
                if (!stepToShow) {
                  return (
                    <Box sx={{ py: 4, textAlign: 'center', color: 'text.tertiary' }}>
                      <Typography level="body-sm">No step available</Typography>
                    </Box>
                  );
                }

                const stepId = stepToShow.id || `temp_${sortedSteps.indexOf(stepToShow)}`;
                const stepIndexForElements = sortedSteps.indexOf(stepToShow);
                const stepElements = elements
                  .filter((el) => {
                    if (stepToShow.id) {
                      return el.stepId === stepToShow.id;
                    }
                    return el.stepId === stepId || el.stepId === stepIndexForElements;
                  })
                  .filter((el) => !el.styles?.hidden && isElementVisible(el, null, { treatMissingTxAsPass: true }))
                  .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

                const animationStyles = {
                  fade: {
                    opacity: isTransitioning ? 0 : 1,
                    transition: 'opacity 0.3s ease-in-out',
                  },
                  slide: {
                    transform: isTransitioning ? 'translateX(100%)' : 'translateX(0)',
                    opacity: isTransitioning ? 0 : 1,
                    transition: 'transform 0.3s ease-in-out, opacity 0.3s ease-in-out',
                  },
                  none: {},
                };

                const handleBackClick = () => {
                  if (currentStepIndex > 0) {
                    navigateToStep(currentStepIndex - 1);
                  }
                };

                return (
                  <Box
                    sx={{
                      ...animationStyles[stepAnimation],
                      position: 'relative',
                      ...getStepStyles(stepToShow),
                    }}
                  >
                    {formStyles?.showBackButton && currentStepIndex > 0 && (
                      <Button
                        variant="outlined"
                        size="sm"
                        onClick={handleBackClick}
                        sx={{ mb: 2 }}
                        startDecorator={
                          formStyles.backButtonIcon ? (
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                              dangerouslySetInnerHTML={{ __html: sanitizeSvg(formStyles.backButtonIcon) }}
                            />
                          ) : (
                            <ArrowBack sx={{ fontSize: 16 }} />
                          )
                        }
                      >
                        {formStyles.backButtonText || 'Back'}
                      </Button>
                    )}
                    <Typography level="title-md" sx={{ mb: 1 }}>
                      {stepToShow.title}
                    </Typography>
                    {stepToShow.description && (
                      <Typography level="body-sm" sx={{ mb: 2, color: 'text.secondary' }}>
                        {stepToShow.description}
                      </Typography>
                    )}
                    <Stack spacing={0}>
                      {stepElements.length === 0 ? (
                        <Typography level="body-sm" sx={{ color: 'text.tertiary', fontStyle: 'italic' }}>
                          No elements in this step
                        </Typography>
                      ) : (
                        stepElements.map((element) => {
                          const globalIndex = elements.findIndex((el) => el === element);
                          const elementKey = element.id || `element_${globalIndex >= 0 ? globalIndex : stepElements.indexOf(element)}`;
                          return (
                            <Box key={elementKey}>
                              {renderElement(element, globalIndex >= 0 ? globalIndex : 0)}
                            </Box>
                          );
                        })
                      )}
                    </Stack>
                  </Box>
                );
              }

              // Single page mode: show all steps
              return (
                <Stack spacing={3}>
                  {sortedSteps.map((step, stepIndex) => {
                    // Match elements to this step
                    const stepId = step.id || `temp_${stepIndex}`;
                    const stepKey = step.id || `step_${stepIndex}`;
                    const stepElements = elements
                      .filter((el) => {
                        if (step.id) {
                          return el.stepId === step.id;
                        }
                        return el.stepId === stepId || el.stepId === stepIndex;
                      })
                      .filter((el) => !el.styles?.hidden && isElementVisible(el, null, { treatMissingTxAsPass: true }))
                      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
                    
                    return (
                      <Box key={stepKey} sx={getStepStyles(step)}>
                        {stepIndex > 0 && <Divider sx={{ my: 2 }} />}
                        <Typography level="title-md" sx={{ mb: 1 }}>
                          {step.title}
                        </Typography>
                        {step.description && (
                          <Typography level="body-sm" sx={{ mb: 2, color: 'text.secondary' }}>
                            {step.description}
                          </Typography>
                        )}
                        <Stack spacing={0}>
                          {stepElements.length === 0 ? (
                            <Typography level="body-sm" sx={{ color: 'text.tertiary', fontStyle: 'italic' }}>
                              No elements in this step
                            </Typography>
                          ) : (
                            stepElements.map((element) => {
                              const globalIndex = elements.findIndex((el) => el === element);
                              const elementKey = element.id || `element_${globalIndex >= 0 ? globalIndex : stepElements.indexOf(element)}`;
                              return (
                                <Box key={elementKey}>
                                  {renderElement(element, globalIndex >= 0 ? globalIndex : 0)}
                                </Box>
                              );
                            })
                          )}
                        </Stack>
                      </Box>
                    );
                  })}
                </Stack>
              );
            } else {
              // Render without steps (backward compatibility)
              const sortedElements = elements
                .filter((el) => !el.stepId)
                .filter((el) => !el.styles?.hidden && isElementVisible(el, null, { treatMissingTxAsPass: true }))
                .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
              
              return sortedElements.length === 0 ? (
                <Box
                  sx={{
                    py: 4,
                    textAlign: 'center',
                    color: 'text.tertiary',
                  }}
                >
                  <Typography level="body-sm">
                    Add elements to see preview
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={0}>
                  {sortedElements.map((element, index) => {
                    const elementKey = element.id || `element_${index}`;
                    return (
                      <Box key={elementKey}>
                        {renderElement(element, index)}
                      </Box>
                    );
                  })}
                </Stack>
              );
            }
          })()}
        </Box>
      </CardContent>
    </Card>
  );
};

export default FormPreview;
